import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { usePolling } from "../hooks/usePolling";
import axios from "axios";
import ConfirmModal from "./ConfirmModal";
import { useCommentActions } from "../hooks/useCommentActions";

// ============================================================
// WHITELIST CACHE — cu TTL 5 min pentru a invalida modificările
// ============================================================
let whitelistCache = null;
let whitelistCacheAt = 0;
const WHITELIST_TTL_MS = 5 * 60 * 1000;

async function getWhitelist(forceRefresh = false) {
  const now = Date.now();
  if (!forceRefresh && whitelistCache && (now - whitelistCacheAt) < WHITELIST_TTL_MS) {
    return whitelistCache;
  }
  try {
    const res = await axios.get(`${process.env.REACT_APP_API || "https://www.vilaro.ro/api"}/whitelist`);
    whitelistCache = new Map((res.data.items || []).map(u => [u.from_id, u.manager || ""]));
    whitelistCacheAt = now;
  } catch (e) {
    if (!whitelistCache) whitelistCache = new Map();
  }
  return whitelistCache;
}

// ============================================================
// UTILS
// ============================================================
function TextWithLinks({ text, style }) {
  if (!text) return null;
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);
  return (
    <span style={style}>
      {parts.map((part, i) =>
        urlRegex.test(part) ? (
          <a key={i} href={part} target="_blank" rel="noreferrer"
            style={{ color: "#a5b4fc", textDecoration: "underline", wordBreak: "break-all" }}
            onClick={e => e.stopPropagation()}>
            {part}
          </a>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </span>
  );
}

function LoadingButton({ onClick, children, loadingText, style, className = "", disabled = false, title }) {
  const [loading, setLoading] = useState(false);
  const handleClick = async (e) => {
    if (loading || disabled) return;
    setLoading(true);
    try { await onClick(e); } finally { setLoading(false); }
  };
  return (
    <button
      onClick={handleClick}
      disabled={loading || disabled}
      title={title}
      className={className + " disabled:opacity-60"}
      style={{ ...style, cursor: loading || disabled ? "not-allowed" : "pointer", display: "inline-flex", alignItems: "center", gap: "6px" }}>
      {loading && (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" style={{ animation: "spin 0.8s linear infinite", flexShrink: 0 }}>
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.25" />
          <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
        </svg>
      )}
      {loading ? (loadingText || children) : children}
    </button>
  );
}

const toastStyles = `
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
@keyframes slideIn { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
@keyframes shrink { from { width: 100%; } to { width: 0%; } }
`;

const API = process.env.REACT_APP_API || "https://www.vilaro.ro/api";

const card = { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" };
const inputStyle = { background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", color: "white", outline: "none" };
const btnGray = { background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.7)" };
const btnPrimary = { background: "linear-gradient(135deg, #6366f1, #8b5cf6)", color: "white" };
const btnGreen = { background: "rgba(34,197,94,0.15)", border: "1px solid rgba(34,197,94,0.3)", color: "#86efac" };
const btnRed = { background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.2)", color: "#fca5a5" };
const btnOrange = { background: "rgba(249,115,22,0.1)", border: "1px solid rgba(249,115,22,0.25)", color: "#fdba74" };
const btnPurple = { background: "rgba(139,92,246,0.15)", border: "1px solid rgba(139,92,246,0.3)", color: "#c4b5fd" };

const classificationStyles = {
  sustinator: { background: "rgba(34,197,94,0.15)", color: "#86efac" },
  critic: { background: "rgba(234,179,8,0.15)", color: "#fde047" },
  troll: { background: "rgba(239,68,68,0.15)", color: "#fca5a5" },
  intrebare: { background: "rgba(99,102,241,0.15)", color: "#a5b4fc" },
  neutru: { background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.5)" },
};

// Indentare uniformă lanț părinți (E3)
const INDENT_PX = 16;
const MAX_INDENT_PX = 48;
const indentFor = (depth) => Math.min(depth * INDENT_PX, MAX_INDENT_PX);

const timeAgo = (dateStr) => {
  if (!dateStr) return "";
  const normalized = dateStr.includes('Z') || dateStr.includes('+') ? dateStr : dateStr.replace(' ', 'T') + 'Z';
  const diff = Date.now() - new Date(normalized).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "acum";
  if (mins < 60) return `${mins} min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.floor(hrs / 24)}z`;
};

// Guard pentru date null/invalide (A6)
const safeLocaleString = (dateStr) => {
  if (!dateStr) return "";
  try {
    const normalized = (typeof dateStr === 'string' && (dateStr.includes('Z') || dateStr.includes('+')))
      ? dateStr
      : (typeof dateStr === 'string' ? dateStr.replace(' ', 'T') + 'Z' : dateStr);
    const d = new Date(normalized);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleString('ro-RO');
  } catch (e) { return ""; }
};

// Build FB permalink cu validare format post_id (F2)
const buildFbPermalink = (postId, commentId, parentId) => {
  if (!postId || typeof postId !== 'string' || !postId.includes('_')) return null;
  const parts = postId.split('_');
  if (parts.length < 2 || !parts[0] || !parts[1]) return null;
  const targetId = (parentId && typeof parentId === 'string' && parentId.includes('_')) ? parentId : commentId;
  const cid = (targetId && targetId.includes('_')) ? targetId.split('_')[1] : targetId;
  return `https://www.facebook.com/permalink.php?story_fbid=${parts[1]}&id=${parts[0]}&comment_id=${cid}`;
};

// Helper pentru styling whitelist
const WhitelistStar = ({ size = "12px" }) => (
  <span title="Susținător protejat" style={{ fontSize: size, marginLeft: "4px" }}>⭐</span>
);

// ============================================================
// USER PROFILE PANEL — cu ⭐ și reclasificare
// ============================================================
function UserProfilePanel({ comment, onClose, onBlock, onUnblock, onReclassified, whitelistIds }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmReclassify, setConfirmReclassify] = useState(null);

  const isProtected = whitelistIds && whitelistIds.has && whitelistIds.has(comment.from_id);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/stats/commenters/${comment.from_id}/chain`);
        if (!cancelled) setData(res.data);
      } catch (e) { console.error(e); }
      if (!cancelled) setLoading(false);
    };
    load();
    return () => { cancelled = true; };
  }, [comment.from_id]);

  const doReclassify = async () => {
    await axios.post(`${API}/comments/${confirmReclassify.commentId}/classify-manual`, { classification: confirmReclassify.cls });
    const res = await axios.get(`${API}/stats/commenters/${comment.from_id}/chain`);
    setData(res.data);
    setConfirmReclassify(null);
    if (onReclassified) onReclassified();
  };

  const totalComments = data ? data.comments.length : (comment.total_comments ?? "—");
  const susCount = data ? data.comments.filter(c => c.classification === 'sustinator').length : (comment.sustinator_count ?? "—");
  const critCount = data ? data.comments.filter(c => c.classification === 'critic').length : (comment.critic_count ?? "—");
  const trollCount = data ? data.comments.filter(c => c.classification === 'troll').length : (comment.troll_count ?? "—");

  return (
    <>
      <ConfirmModal
        open={!!confirmReclassify}
        title="Reclasifică comentariul"
        text={
          <>
            <span className="block mb-1">Clasificare nouă:</span>
            {confirmReclassify && (
              <span className="text-sm px-2 py-0.5 rounded-full font-medium inline-block" style={classificationStyles[confirmReclassify.cls] || classificationStyles.neutru}>
                {confirmReclassify.cls}
              </span>
            )}
          </>
        }
        confirmLabel="Confirmă"
        loadingLabel="Se aplică..."
        onConfirm={doReclassify}
        onCancel={() => setConfirmReclassify(null)}
      />

      <div className="fixed right-0 top-0 h-full z-40 overflow-y-auto" style={{ background: "#0f0f1a", borderLeft: "1px solid rgba(99,102,241,0.3)", width: "min(384px, 100vw)" }}>
        <div className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-white">Profil Comentator</h3>
            <button onClick={onClose} className="text-xs px-2 py-1 rounded-lg" style={btnGray}>Închide</button>
          </div>

          <div className="flex items-center gap-3 mb-3">
            <div className="rounded-full w-12 h-12 flex-shrink-0 flex items-center justify-center text-lg font-bold"
              style={{ background: "linear-gradient(135deg, #6366f1, #8b5cf6)", border: "2px solid rgba(99,102,241,0.3)", color: "white" }}>
              {comment.from_name ? comment.from_name.charAt(0).toUpperCase() : "?"}
            </div>
            <div>
              <p className="font-medium text-white flex items-center">
                {comment.from_name}
                {isProtected && <WhitelistStar size="14px" />}
              </p>
              {comment.classification && <span className="text-xs px-2 py-0.5 rounded-full" style={classificationStyles[comment.classification] || classificationStyles.neutru}>{comment.classification}</span>}
              {whitelistIds && whitelistIds.has && whitelistIds.has(comment.from_id) && whitelistIds.get(comment.from_id) && (
                <span className="text-xs px-2 py-0.5 rounded-full ml-1" style={{ background: "rgba(234,179,8,0.15)", color: "#fde047", border: "1px solid rgba(234,179,8,0.3)" }}>
                  ⭐ {whitelistIds.get(comment.from_id)}
                </span>
              )}
            </div>
          </div>

          {isProtected && (
            <div className="rounded-xl px-3 py-2 mb-3 text-xs" style={{ background: "rgba(234,179,8,0.08)", border: "1px solid rgba(234,179,8,0.25)", color: "#fde047" }}>
              ⭐ Susținător protejat — nu poate fi ascuns sau blocat
            </div>
          )}

          <div className="flex items-center gap-2 mb-4">
            <p className="text-xs" style={{ color: "rgba(255,255,255,0.4)" }}>ID: {comment.from_id}</p>
            {comment.from_name && (
              <a href={`https://www.facebook.com/search/people/?q=${encodeURIComponent(comment.from_name)}`} target="_blank" rel="noreferrer"
                className="text-xs px-2 py-0.5 rounded-lg"
                style={{ background: "rgba(99,102,241,0.15)", color: "#a5b4fc", border: "1px solid rgba(99,102,241,0.3)" }}>
                🔍 Caută pe FB
              </a>
            )}
          </div>

          <div className="grid grid-cols-2 gap-2 mb-4">
            {[
              { label: "Total", value: totalComments, color: "white" },
              { label: "Susținători", value: susCount, color: "#86efac" },
              { label: "Critici", value: critCount, color: "#fde047" },
              { label: "Troli", value: trollCount, color: "#fca5a5" },
            ].map((s, i) => (
              <div key={i} className="rounded-xl p-3 text-center" style={card}>
                <p className="text-xl font-bold" style={{ color: s.color }}>{s.value}</p>
                <p className="text-xs" style={{ color: "rgba(255,255,255,0.3)" }}>{s.label}</p>
              </div>
            ))}
          </div>

          {!isProtected && (
            comment.is_blocked
              ? <LoadingButton onClick={onUnblock} loadingText="Se deblochează..." className="w-full py-2 rounded-xl text-sm font-medium mb-4 justify-center" style={btnOrange}>Deblochează utilizatorul</LoadingButton>
              : <LoadingButton onClick={onBlock} loadingText="Se blochează..." className="w-full py-2 rounded-xl text-sm font-medium mb-4 justify-center" style={btnOrange}>Blochează utilizatorul</LoadingButton>
          )}

          <div className="border-t mb-4" style={{ borderColor: "rgba(255,255,255,0.06)" }}></div>
          <h4 className="text-xs font-semibold mb-3" style={{ color: "rgba(255,255,255,0.4)" }}>
            TOATE COMENTARIILE ({data ? data.comments.length : (comment.total_comments || 0)})
          </h4>

          {loading && <div className="text-xs py-4 text-center" style={{ color: "rgba(255,255,255,0.3)" }}>Se incarca...</div>}

          {data && (
            <div className="space-y-3">
              {data.comments.map(c => {
                const fbLink = buildFbPermalink(c.post_id, c.id, c.parent_id);
                return (
                  <div key={c.id} className="rounded-xl p-3" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)" }}>
                    {c.post_message && (
                      <div className="rounded-lg px-2 py-1.5 mb-2 text-xs" style={{ background: "rgba(99,102,241,0.06)", borderLeft: "2px solid rgba(99,102,241,0.3)" }}>
                        <span style={{ color: "rgba(255,255,255,0.3)" }}>📄 </span>
                        <span style={{ color: "rgba(255,255,255,0.4)" }}>{c.post_message?.substring(0, 60)}...</span>
                      </div>
                    )}
                    {c.parent_chain && c.parent_chain.length > 0 && (
                      <div className="space-y-1 mb-2">
                        {c.parent_chain.map((p, i) => (
                          <div key={p.id} className="rounded-lg px-2 py-1.5 text-xs flex items-start gap-1"
                            style={{ background: "rgba(255,255,255,0.02)", borderLeft: "2px solid rgba(255,255,255,0.06)", marginLeft: `${indentFor(i)}px` }}>
                            <span style={{ color: "rgba(255,255,255,0.2)" }}>↳</span>
                            <div>
                              <span className="font-medium" style={{ color: "rgba(255,255,255,0.4)" }}>
                                {p.from_name}
                                {whitelistIds && whitelistIds.has && whitelistIds.has(p.from_id) && <WhitelistStar />}
                                :{" "}
                              </span>
                              <span style={{ color: "rgba(255,255,255,0.3)" }}>{p.message?.substring(0, 60)}</span>
                              {p.classification && <span className="ml-1 px-1.5 py-0.5 rounded-full text-xs" style={classificationStyles[p.classification] || classificationStyles.neutru}>{p.classification}</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="rounded-lg px-2 py-1.5 mb-2" style={{ background: "rgba(99,102,241,0.05)", borderLeft: `2px solid ${c.classification === 'troll' ? '#fca5a5' : c.classification === 'critic' ? '#fde047' : c.classification === 'sustinator' ? '#86efac' : 'rgba(99,102,241,0.3)'}` }}>
                      <p className="text-xs" style={{ color: "rgba(255,255,255,0.85)" }}>{c.message}</p>
                    </div>
                    <div className="flex items-center gap-1 flex-wrap mt-1">
                      <span className="text-xs mr-1" style={{ color: "rgba(255,255,255,0.25)" }}>Reclasifică:</span>
                      {["sustinator", "critic", "troll", "intrebare", "neutru"].map(cls => (
                        <button key={cls} onClick={() => setConfirmReclassify({ commentId: c.id, cls })}
                          className="text-xs px-1.5 py-0.5 rounded-full transition-all"
                          style={c.classification === cls ? classificationStyles[cls] : {
                            background: cls === 'sustinator' ? "rgba(34,197,94,0.08)" : cls === 'critic' ? "rgba(234,179,8,0.08)" : cls === 'troll' ? "rgba(239,68,68,0.08)" : cls === 'intrebare' ? "rgba(99,102,241,0.08)" : "rgba(255,255,255,0.05)",
                            color: cls === 'sustinator' ? "rgba(134,239,172,0.75)" : cls === 'critic' ? "rgba(253,224,71,0.75)" : cls === 'troll' ? "rgba(252,165,165,0.75)" : cls === 'intrebare' ? "rgba(165,180,252,0.75)" : "rgba(255,255,255,0.5)",
                            border: cls === 'sustinator' ? "1px solid rgba(34,197,94,0.25)" : cls === 'critic' ? "1px solid rgba(234,179,8,0.25)" : cls === 'troll' ? "1px solid rgba(239,68,68,0.25)" : cls === 'intrebare' ? "1px solid rgba(99,102,241,0.25)" : "1px solid rgba(255,255,255,0.1)"
                          }}>
                          {cls}
                        </button>
                      ))}
                      <span className="text-xs ml-auto" style={{ color: "rgba(255,255,255,0.2)" }}>{safeLocaleString(c.created_time)}</span>
                      {fbLink && (
                        <a href={fbLink} target="_blank" rel="noreferrer"
                          className="text-xs px-1.5 py-0.5 rounded-lg ml-1"
                          style={{ background: "rgba(99,102,241,0.15)", color: "#a5b4fc" }}>
                          🔗 FB
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ============================================================
// POST MESSAGE — postare cu expand/collapse
// ============================================================
function PostMessage({ message, maxLen = 200 }) {
  const [expanded, setExpanded] = useState(false);
  if (!message) return null;
  const isLong = message.length > maxLen;
  return (
    <div className="rounded-lg px-3 py-2 mb-2 text-xs" style={{ background: "rgba(99,102,241,0.06)", borderLeft: "2px solid rgba(99,102,241,0.3)" }}>
      <span className="font-semibold block mb-1" style={{ color: "rgba(99,102,241,0.8)" }}>📄 Postarea:</span>
      <span style={{ color: "rgba(255,255,255,0.5)" }}>
        {isLong && !expanded ? message.substring(0, maxLen) + "..." : message}
      </span>
      {isLong && (
        <button onClick={() => setExpanded(!expanded)} className="ml-2 text-xs" style={{ color: "#a5b4fc" }}>
          {expanded ? "Arată mai puțin" : "Arată mai mult"}
        </button>
      )}
    </div>
  );
}
// ============================================================
// INBOX ITEM — cu protecție whitelist și LoadingButton peste tot
// ============================================================
function InboxItem({ comment, pages, tab, onHide, onUnhide, onBlock, onUnblock, onResolve, onUnresolve, onGoToHistoric, onShowProfile, onDelete, onReplyAndUnhide, slaStatus, isSelected, onToggleSelect, showCheckbox, onReplyOpen, whitelistIds = new Map(), onProtectedAttempt }) {
  const [showReply, setShowReply] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [selectedPage, setSelectedPage] = useState(comment.page_id);
  const [showNewComment, setShowNewComment] = useState(false);
  const [newCommentText, setNewCommentText] = useState("");
  const [newCommentPage, setNewCommentPage] = useState(comment.page_id);

  const isProtected = whitelistIds && whitelistIds.has && whitelistIds.has(comment.from_id);

  // Filtrare pagini compatibile pentru "Comentariu nou" — cu useMemo (D5)
  const compatiblePages = useMemo(() => {
    const currentPageAccountId = pages.find(pg => pg.id === comment.page_id)?.account_id;
    return pages.filter(p =>
      (p.page_type === 'comment_support' || p.page_type === 'official') &&
      String(p.account_id) === String(currentPageAccountId)
    );
  }, [pages, comment.page_id]);

  const handleNewComment = async () => {
    if (!newCommentText.trim() || !newCommentPage) return;
    try {
      await axios.post(`${API}/posts/${comment.post_id}/comment?page_id=${newCommentPage}`, { message: newCommentText });
      setNewCommentText(""); setShowNewComment(false);
    } catch (e) { console.error(e); }
  };

  const cls = comment.classification;
  const clsStyle = classificationStyles[cls] || classificationStyles.neutru;
  const primaryAction = cls === 'troll' ? 'block' : (cls === 'critic' || cls === 'intrebare') ? 'reply' : 'resolve';
  const borderColor = cls === 'troll' ? '#fca5a5' : cls === 'critic' ? '#fde047' : cls === 'sustinator' ? '#86efac' : cls === 'intrebare' ? '#a5b4fc' : isProtected ? '#fde047' : 'rgba(255,255,255,0.1)';

  const sendingRef = useRef(false);
  const handleReply = async () => {
    if (!replyText.trim()) return;
    if (sendingRef.current) return; // protecție anti-double-submit
    sendingRef.current = true;
    try {
      if (tab === "ascunse" && onReplyAndUnhide) {
        await onReplyAndUnhide(comment.id, replyText, selectedPage);
      } else {
        await axios.post(`${API}/comments/${comment.id}/reply?page_id=${selectedPage}`, { message: replyText });
      }
      setReplyText(""); setShowReply(false);
      if (onReplyOpen) onReplyOpen(false);
    } catch (e) { console.error(e); }
    finally { sendingRef.current = false; }
  };

  const handleGenerateAi = async () => {
    try {
      // Endpoint inteligent — backend construieste singur thread-ul complet din BD
      const rr = await axios.post(`${API}/ai/generate-reply-for-comment/${comment.id}`);
      setReplyText(rr.data.reply);
    } catch (e) { console.error(e); }
  };

  // Wrappere care verifică protecția whitelist înainte de acțiune
  const safeOnHide = () => {
    if (isProtected) { onProtectedAttempt?.(comment.from_name, "ascuns"); return; }
    onHide(comment.id);
  };
  const safeOnBlock = () => {
    if (isProtected) { onProtectedAttempt?.(comment.from_name, "blocat"); return; }
    onBlock(comment.id);
  };

  return (
    <div className="rounded-2xl p-4" style={{ ...card, borderLeft: `3px solid ${borderColor}`, outline: isSelected ? "1px solid rgba(99,102,241,0.5)" : "none" }}>
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-1.5 flex-wrap mt-1">
          {showCheckbox && !isProtected && (
            <input type="checkbox" checked={isSelected} onChange={() => onToggleSelect(comment.id)}
              className="accent-indigo-500 w-4 h-4 flex-shrink-0 cursor-pointer" />
          )}
          <button className="text-sm font-semibold text-white hover:text-indigo-300 transition-colors" onClick={() => onShowProfile(comment)}>
            {comment.from_name}
            {isProtected && <WhitelistStar />}
          </button>
          {cls && <span className="text-xs px-2 py-0.5 rounded-full" style={clsStyle}>{cls}</span>}
          {comment.total_comments > 1 && <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.4)" }}>{comment.total_comments} comentarii</span>}
          {(() => {
            const types = [];
            const curCls = comment.classification;
            if (comment.troll_count > (curCls === 'troll' ? 1 : 0)) types.push({ label: "troll", bg: "rgba(239,68,68,0.1)", color: "#fca5a5" });
            if (comment.critic_count > (curCls === 'critic' ? 1 : 0)) types.push({ label: "critic", bg: "rgba(234,179,8,0.1)", color: "#fde047" });
            if (comment.sustinator_count > (curCls === 'sustinator' ? 1 : 0)) types.push({ label: "susținător", bg: "rgba(34,197,94,0.1)", color: "#86efac" });
            if (types.length > 1) return <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: "rgba(99,102,241,0.1)", color: "#a5b4fc" }}>mixt</span>;
            if (types.length === 1) return <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: types[0].bg, color: types[0].color }}>{types[0].label} cunoscut</span>;
            return null;
          })()}
          {comment.is_hidden === 1 && <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.4)" }}>ascuns</span>}
          {comment.is_blocked === 1 && <span className="text-xs px-2 py-0.5 rounded-full" style={btnOrange}>blocat</span>}
        </div>
        <div className="flex flex-col items-end gap-1 flex-shrink-0 ml-2">
          <div className="flex items-center gap-1.5">
            {slaStatus === "red" && <span className="text-xs px-1.5 py-0.5 rounded-full font-bold" style={{ background: "rgba(239,68,68,0.2)", color: "#fca5a5" }}>🔴</span>}
            {slaStatus === "yellow" && <span className="text-xs px-1.5 py-0.5 rounded-full font-bold" style={{ background: "rgba(234,179,8,0.2)", color: "#fde047" }}>🟡</span>}
            {slaStatus === "green" && <span className="text-xs px-1.5 py-0.5 rounded-full font-bold" style={{ background: "rgba(34,197,94,0.2)", color: "#86efac" }}>🟢</span>}
            <span className="text-xs" style={{ color: "rgba(255,255,255,0.3)" }}>{timeAgo(comment.created_time)}</span>
          </div>
          <span className="text-xs" style={{ color: "rgba(255,255,255,0.45)" }}>
            {safeLocaleString(comment.created_time)}
          </span>
        </div>
      </div>

      <div className="flex items-center gap-1 mb-2 flex-wrap">
        <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: "rgba(99,102,241,0.1)", color: "#a5b4fc" }}>{comment.page_name}</span>
      </div>
      {comment.post_message && <PostMessage message={comment.post_message} />}

      {comment.parent_chain && comment.parent_chain.length > 0 && (
        <div className="space-y-1 mb-1">
          {comment.parent_chain.map((p, i) => (
            <div key={p.id} className="rounded-lg px-3 py-1.5"
              style={{ background: "rgba(255,255,255,0.02)", borderLeft: "2px solid rgba(255,255,255,0.08)", marginLeft: `${indentFor(i)}px` }}>
              <span className="text-xs font-semibold" style={{ color: "rgba(255,255,255,0.5)" }}>
                💬 <button onClick={() => onShowProfile({ ...comment, from_id: p.from_id, from_name: p.from_name, classification: p.classification })}
                  className="hover:text-indigo-300 transition-colors"
                  style={{ color: "rgba(255,255,255,0.7)", background: "none", border: "none", cursor: "pointer", padding: 0 }}>
                  {p.from_name}
                </button>
                {whitelistIds && whitelistIds.has && whitelistIds.has(p.from_id) && <WhitelistStar />}
                :
              </span>
              {p.classification && <span className="ml-1 px-1.5 py-0.5 rounded-full text-xs" style={classificationStyles[p.classification] || classificationStyles.neutru}>{p.classification}</span>}
              <p className="text-xs mt-0.5" style={{ color: "rgba(255,255,255,0.5)" }}>{p.message}</p>
            </div>
          ))}
        </div>
      )}
      {!comment.parent_chain && comment.parent_message && (
        <div className="rounded-lg px-3 py-1.5 mb-1" style={{ background: "rgba(255,255,255,0.02)", borderLeft: "2px solid rgba(255,255,255,0.08)" }}>
          <span className="text-xs font-semibold" style={{ color: "rgba(255,255,255,0.5)" }}>
            💬 <span style={{ color: "rgba(255,255,255,0.7)" }}>{comment.parent_from_name}</span>:
          </span>
          <p className="text-xs mt-0.5" style={{ color: "rgba(255,255,255,0.5)" }}>{comment.parent_message}</p>
        </div>
      )}

      {comment.mention && (
        <div className="rounded-lg px-3 py-1.5 mb-1"
          style={{
            background: "rgba(167,139,250,0.06)",
            borderLeft: "2px solid rgba(167,139,250,0.4)",
            marginLeft: comment.parent_chain && comment.parent_chain.length > 0 ? `${indentFor(comment.parent_chain.length)}px` : "0px"
          }}>
          <span className="text-xs font-semibold" style={{ color: "rgba(167,139,250,0.85)" }}>
            💡 răspunde la <button onClick={() => onShowProfile({ ...comment, from_id: comment.mention.from_id, from_name: comment.mention.from_name, classification: comment.mention.classification })}
              className="hover:text-indigo-200 transition-colors"
              style={{ color: "#c4b5fd", background: "none", border: "none", cursor: "pointer", padding: 0, fontWeight: "bold" }}>
              {comment.mention.from_name}
            </button>
            {comment.mention.classification && <span className="ml-1 px-1.5 py-0.5 rounded-full text-xs" style={classificationStyles[comment.mention.classification] || classificationStyles.neutru}>{comment.mention.classification}</span>}
            :
          </span>
          <p className="text-xs mt-0.5 italic" style={{ color: "rgba(255,255,255,0.55)" }}>
            «{comment.mention.message && comment.mention.message.length > 200 ? comment.mention.message.slice(0, 200) + '…' : comment.mention.message}»
          </p>
        </div>
      )}

      <div className="rounded-lg px-3 py-2 mb-2"
        style={{
          background: "rgba(255,255,255,0.04)",
          border: "1px solid rgba(255,255,255,0.1)",
          marginLeft: comment.parent_chain && comment.parent_chain.length > 0 ? `${indentFor(comment.parent_chain.length)}px` : "0px"
        }}>
        <span className="text-xs font-semibold block mb-1" style={{ color: "rgba(255,255,255,0.4)" }}>
          💬 <button onClick={() => onShowProfile(comment)}
            className="hover:text-indigo-300 transition-colors"
            style={{ color: "white", background: "none", border: "none", cursor: "pointer", padding: 0, fontWeight: "bold" }}>
            {comment.from_name}
          </button>
          {isProtected && <WhitelistStar />}
          :
        </span>
        <TextWithLinks text={comment.message} style={{ color: "rgba(255,255,255,0.85)", fontSize: "0.875rem", display: "block" }} />
      </div>

      {buildFbPermalink(comment.post_id, comment.id, comment.parent_id) && (
        <a href={buildFbPermalink(comment.post_id, comment.id, comment.parent_id)} target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 mb-2 text-xs font-medium transition-all"
          style={{
            background: "rgba(59,130,246,0.12)",
            border: "1px solid rgba(59,130,246,0.35)",
            color: "#93c5fd",
            textDecoration: "none",
            marginLeft: comment.parent_chain && comment.parent_chain.length > 0 ? `${indentFor(comment.parent_chain.length)}px` : "0px"
          }}>
          🔗 Link comentariu FB
        </a>
      )}

      {(() => {
        // Single source of truth: afisam DOAR children (confirmate de webhook FB).
        // Reply-urile noastre apar in children prin webhook (own_page comments).
        // Tabela replies ramane pentru audit/statistici, nu o afisam in UI.
        const allReplies = (comment.children || []).slice().sort((a, b) =>
          new Date(a.created_time) - new Date(b.created_time)
        );
        if (allReplies.length === 0) return null;
        const baseIndent = comment.parent_chain ? comment.parent_chain.length : 0;

        return (
          <div className="space-y-1 mb-3">
            {allReplies.map((r) => {
              const totalIndent = indentFor(baseIndent + (r.depth || 0) + 1);
              const isOurs = r.is_ours;
              const isChildProtected = r.from_id && whitelistIds && whitelistIds.has && whitelistIds.has(r.from_id);
              return (
                <div key={r.id} className="rounded-lg px-3 py-2"
                  style={{
                    background: isOurs ? "rgba(34,197,94,0.08)" : "rgba(255,255,255,0.02)",
                    borderLeft: `2px solid ${isOurs ? "rgba(34,197,94,0.4)" : "rgba(255,255,255,0.08)"}`,
                    marginLeft: `${totalIndent}px`
                  }}>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-xs font-semibold" style={{ color: isOurs ? "rgba(34,197,94,0.8)" : "rgba(255,255,255,0.5)" }}>
                      {isOurs ? "↩" : "💬"}{" "}
                      {isOurs ? (
                        <span style={{ color: "#86efac" }}>{r.from_name}</span>
                      ) : (
                        <button onClick={() => onShowProfile({ id: r.id, from_id: r.from_id, from_name: r.from_name, classification: r.classification, page_id: comment.page_id })}
                          style={{ color: "rgba(255,255,255,0.7)", background: "none", border: "none", cursor: "pointer", padding: 0, fontWeight: "bold" }}>
                          {r.from_name}
                        </button>
                      )}
                      {isChildProtected && <WhitelistStar />}:
                    </span>
                    {r.classification && !isOurs && (
                      <span className="text-xs px-1.5 py-0.5 rounded-full" style={classificationStyles[r.classification] || classificationStyles.neutru}>
                        {r.classification}
                      </span>
                    )}
                  </div>
                  <p className="text-xs mt-0.5" style={{ color: "rgba(255,255,255,0.85)" }}>{r.message}</p>
                </div>
              );
            })}
          </div>
        );
      })()}

      {showReply && (
        <div className="mb-3 space-y-2 p-3 rounded-xl" style={{ background: "rgba(99,102,241,0.05)", border: "1px solid rgba(99,102,241,0.15)" }}>
          <select className="w-full rounded-xl px-3 py-2 text-xs" style={inputStyle} value={selectedPage} onChange={e => setSelectedPage(e.target.value)}>
            {pages.map(p => <option key={p.id} value={p.id} style={{ color: "black" }}>{p.name}</option>)}
          </select>
          <textarea className="w-full rounded-xl px-3 py-2 text-sm resize-none" style={inputStyle}
            placeholder="Scrie un răspuns..." rows={2} value={replyText} onChange={e => setReplyText(e.target.value)} />
          <div className="flex gap-2">
            <LoadingButton onClick={handleGenerateAi} loadingText="Se generează..." className="px-3 py-1.5 rounded-lg text-xs font-medium" style={btnPurple}>✨ AI</LoadingButton>
            <LoadingButton onClick={handleReply} disabled={!replyText.trim()} loadingText="Se trimite..." className="px-4 py-1.5 rounded-lg text-xs font-medium" style={btnPrimary}>
              {tab === "ascunse" ? "Afișează și trimite" : "Trimite"}
            </LoadingButton>
            <button onClick={() => { setShowReply(false); if (onReplyOpen) onReplyOpen(false); }} className="px-3 py-1.5 rounded-lg text-xs" style={btnGray}>Anulează</button>
          </div>
        </div>
      )}

      {showNewComment && (
        <div className="mb-3 space-y-2 p-3 rounded-xl" style={{ background: "rgba(34,197,94,0.05)", border: "1px solid rgba(34,197,94,0.15)" }}>
          <p className="text-xs font-medium" style={{ color: "#86efac" }}>💬 Comentariu nou pe această postare</p>
          {compatiblePages.length === 0 ? (
            <p className="text-xs" style={{ color: "#fdba74" }}>⚠️ Nicio pagină compatibilă în acest cont (necesară: official sau comment_support)</p>
          ) : (
            <select className="w-full rounded-xl px-3 py-2 text-xs" style={inputStyle} value={newCommentPage} onChange={e => setNewCommentPage(e.target.value)}>
              {compatiblePages.map(p => <option key={p.id} value={p.id} style={{ color: "black" }}>{p.name}</option>)}
            </select>
          )}
          <textarea className="w-full rounded-xl px-3 py-2 text-sm resize-none" style={inputStyle}
            placeholder="Scrie un comentariu nou..." rows={2} value={newCommentText} onChange={e => setNewCommentText(e.target.value)} />
          <div className="flex gap-2">
            <LoadingButton onClick={handleNewComment} disabled={!newCommentText.trim() || compatiblePages.length === 0}
              loadingText="Se postează..." className="px-4 py-1.5 rounded-lg text-xs font-medium" style={btnGreen}>
              Postează
            </LoadingButton>
            <button onClick={() => setShowNewComment(false)} className="px-3 py-1.5 rounded-lg text-xs" style={btnGray}>Anulează</button>
          </div>
        </div>
      )}

      <div className="flex items-center gap-1.5 flex-wrap mt-1">
        {tab === "nerezolvate" && primaryAction === 'block' && !comment.is_blocked && !isProtected && (
          <LoadingButton onClick={safeOnBlock} loadingText="Blochează..." className="px-3 py-1.5 rounded-xl text-xs font-medium" style={btnOrange}>Blochează</LoadingButton>
        )}
        {tab === "nerezolvate" && (
          <button onClick={() => { setShowNewComment(!showNewComment); if (!showNewComment) setNewCommentText(`@${comment.from_name} `); }} className="px-3 py-1.5 rounded-xl text-xs font-medium" style={showNewComment ? btnGray : btnGreen}>+ Comentariu nou</button>
        )}
        {tab === "nerezolvate" && primaryAction === 'reply' && (
          <button onClick={() => { const next = !showReply; setShowReply(next); if (onReplyOpen) onReplyOpen(next); }} className="px-3 py-1.5 rounded-xl text-xs font-medium" style={showReply ? btnGray : btnPrimary}>Răspunde</button>
        )}
        {tab === "nerezolvate" && primaryAction === 'resolve' && (
          <LoadingButton onClick={() => onResolve(comment.id)} loadingText="Se rezolvă..." className="px-3 py-1.5 rounded-xl text-xs font-medium" style={btnGreen}>Rezolvat</LoadingButton>
        )}
        {tab === "ascunse" && (
          <>
            <LoadingButton onClick={() => onUnhide(comment.id)} loadingText="Se afișează..." className="px-3 py-1.5 rounded-xl text-xs font-medium" style={btnGreen}>Afișează</LoadingButton>
            <button onClick={() => { const next = !showReply; setShowReply(next); if (onReplyOpen) onReplyOpen(next); }} className="px-3 py-1.5 rounded-xl text-xs font-medium" style={showReply ? btnGray : btnPrimary}>Răspunde</button>
          </>
        )}
        {tab === "blocate" && (
          <LoadingButton onClick={() => onUnblock(comment.id)} loadingText="Se deblochează..." className="px-3 py-1.5 rounded-xl text-xs font-medium" style={btnGreen}>Deblochează</LoadingButton>
        )}
        {tab === "rezolvate" && (
          <LoadingButton onClick={() => onUnresolve(comment.id)} loadingText="Se redeschide..." className="px-3 py-1.5 rounded-xl text-xs font-medium" style={btnGray}>Redeschide</LoadingButton>
        )}

        <div className="flex items-center gap-1 ml-auto flex-wrap justify-end">
          {tab === "nerezolvate" && primaryAction !== 'reply' && (
            <button onClick={() => setShowReply(!showReply)} className="px-2 py-1 rounded-lg text-xs" style={btnGray}>Răspunde</button>
          )}
          {tab === "nerezolvate" && comment.is_hidden === 0 && !isProtected && (
            <LoadingButton onClick={safeOnHide} loadingText="..." className="px-2 py-1 rounded-lg text-xs" style={btnGray}>Ascunde</LoadingButton>
          )}
          {tab === "nerezolvate" && comment.is_hidden === 1 && (
            <LoadingButton onClick={() => onUnhide(comment.id)} loadingText="..." className="px-2 py-1 rounded-lg text-xs" style={btnGray}>Afișează</LoadingButton>
          )}
          {tab === "nerezolvate" && !comment.is_blocked && primaryAction !== 'block' && !isProtected && (
            <LoadingButton onClick={safeOnBlock} loadingText="..." className="px-2 py-1 rounded-lg text-xs" style={btnGray}>Blochează</LoadingButton>
          )}
          {tab === "nerezolvate" && primaryAction !== 'resolve' && (
            <LoadingButton onClick={() => onResolve(comment.id)} loadingText="..." className="px-2 py-1 rounded-lg text-xs" style={btnGray}>Rezolvat</LoadingButton>
          )}
          {tab === "ascunse" && !isProtected && (
            <LoadingButton onClick={safeOnBlock} loadingText="..." className="px-2 py-1 rounded-lg text-xs" style={btnOrange}>Blochează</LoadingButton>
          )}
          {tab === "ascunse" && (
            <LoadingButton onClick={() => onResolve(comment.id)} loadingText="..." className="px-2 py-1 rounded-lg text-xs" style={btnGray}>Rezolvat</LoadingButton>
          )}
          <button onClick={() => onGoToHistoric(comment)} className="px-2 py-1 rounded-lg text-xs" style={btnGray}>Istoric</button>
          <LoadingButton onClick={() => onDelete(comment.id)} loadingText="..." className="px-2 py-1 rounded-lg text-xs" style={btnRed}>Șterge</LoadingButton>
        </div>
      </div>
    </div>
  );
}
// ============================================================
// INBOX HEADER — tab-uri + filtre (sub-componenta E5)
// ============================================================
function InboxHeader({ inboxTabs, tab, onTab, accounts, pages, filterAccount, filterPage, onFilterAccount, onFilterPage, search, onSearch, classFilters, filter, onFilter, autopilot, onToggleAutopilot, onRefresh }) {
  return (
    <>
      <div className="mb-5 p-1 rounded-xl" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
        <div className="flex gap-0.5 mb-0.5">
          {inboxTabs.slice(0, 3).map(t => (
            <button key={t.id} onClick={() => onTab(t.id)}
              className="flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-all flex items-center justify-center gap-1.5"
              style={tab === t.id ? btnPrimary : { color: "rgba(255,255,255,0.4)" }}>
              {t.label}
              {t.count > 0 && (
                <span className="text-xs px-1.5 py-0.5 rounded-full font-bold"
                  style={{ background: tab === t.id ? "rgba(255,255,255,0.2)" : "rgba(239,68,68,0.15)", color: tab === t.id ? "white" : t.countColor }}>
                  {t.count}
                </span>
              )}
            </button>
          ))}
        </div>
        <div className="flex gap-0.5">
          {inboxTabs.slice(3).map(t => (
            <button key={t.id} onClick={() => onTab(t.id)}
              className="flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-all flex items-center justify-center gap-1.5"
              style={tab === t.id ? btnPrimary : { color: "rgba(255,255,255,0.4)" }}>
              {t.label}
              {t.count > 0 && (
                <span className="text-xs px-1.5 py-0.5 rounded-full font-bold"
                  style={{ background: tab === t.id ? "rgba(255,255,255,0.2)" : "rgba(239,68,68,0.15)", color: tab === t.id ? "white" : t.countColor }}>
                  {t.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-4 mb-4 flex-wrap">
        <div>
          <p className="text-xs mb-1 font-medium" style={{ color: "rgba(255,255,255,0.6)" }}>Conturi</p>
          <div className="flex gap-2">
            <button onClick={() => onFilterAccount("")}
              className="px-4 py-2 rounded-xl text-sm font-medium transition-all"
              style={!filterAccount ? btnPrimary : btnGray}>Toate</button>
            {accounts.map(a => (
              <button key={a.id} onClick={() => onFilterAccount(String(a.id))}
                className="px-4 py-2 rounded-xl text-sm font-medium transition-all"
                style={String(filterAccount) === String(a.id) ? btnPrimary : btnGray}>{a.name}</button>
            ))}
          </div>
        </div>
        <div>
          <p className="text-xs mb-1 font-medium" style={{ color: "rgba(255,255,255,0.6)" }}>Pagini</p>
          <select className="rounded-xl px-3 py-2 text-sm" style={inputStyle}
            value={filterPage} onChange={e => onFilterPage(e.target.value)}>
            <option value="" style={{ color: "black" }}>Toate paginile</option>
            {pages.filter(p => !filterAccount || String(p.account_id) === String(filterAccount)).map(p => (
              <option key={p.id} value={p.id} style={{ color: "black" }}>{p.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="mb-4">
        <input
          type="text"
          placeholder="🔍 Caută după nume sau text comentariu..."
          className="w-full rounded-xl px-4 py-2.5 text-sm"
          style={inputStyle}
          value={search}
          onChange={e => onSearch(e.target.value)}
        />
      </div>

      <div className="flex gap-2 mb-4 flex-wrap items-center">
        {classFilters.map(f => (
          <button key={f.id} onClick={() => onFilter(f.id)}
            className="px-3 py-1.5 rounded-xl text-xs font-medium transition-all"
            style={filter === f.id ? btnPrimary : btnGray}>{f.label}</button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <LoadingButton onClick={onToggleAutopilot}
            loadingText="..."
            title="Pilot automat — blochează trolii detectați prin webhook + generează reply-uri pentru aprobare"
            className="px-3 py-1.5 rounded-xl text-xs font-medium"
            style={autopilot ? { background: "rgba(34,197,94,0.15)", border: "1px solid rgba(34,197,94,0.3)", color: "#86efac" } : { background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.2)", color: "rgba(252,165,165,0.6)" }}>
            🤖 Pilot {autopilot ? "ON" : "OFF"}
          </LoadingButton>
          <LoadingButton onClick={onRefresh} loadingText="..." className="px-3 py-1.5 rounded-xl text-xs" style={btnGray} title="Reîncarcă comentariile din baza de date">🔄</LoadingButton>
        </div>
      </div>
    </>
  );
}

// ============================================================
// PENDING LIST — lista reply-urilor de aprobat (sub-componenta E5)
// ============================================================
function PendingList({ pendingItems, editingReply, setEditingReply, approveReply, rejectReply, regenerateReply, setProfileComment, whitelistIds, getSlaStatus }) {
  if (pendingItems.length === 0) {
    return (
      <div className="text-center py-16 text-sm" style={{ color: "rgba(255,255,255,0.2)" }}>
        🤖 Niciun răspuns în așteptare
      </div>
    );
  }
  return (
    <div className="space-y-4">
      {pendingItems.map(item => {
        const isProtected = whitelistIds && whitelistIds.has && whitelistIds.has(item.from_id);
        const cls = item.classification;
        const borderLeftColor = cls === 'troll' ? '#fca5a5' : cls === 'critic' ? '#fde047' : cls === 'sustinator' ? '#86efac' : cls === 'intrebare' ? '#a5b4fc' : isProtected ? '#fde047' : 'rgba(99,102,241,0.4)';
        return (
          <div key={item.reply_id} className="rounded-2xl p-4" style={{ background: "rgba(99,102,241,0.05)", border: "1px solid rgba(99,102,241,0.2)", borderLeft: `3px solid ${borderLeftColor}` }}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-1.5 flex-wrap mt-1">
                <button className="font-semibold text-sm text-white hover:text-indigo-300 transition-colors"
                  onClick={() => setProfileComment({
                    id: item.comment_id,
                    from_id: item.from_id,
                    from_name: item.from_name,
                    classification: item.classification,
                    is_blocked: 0,
                    page_id: item.page_id
                  })}>
                  {item.from_name}
                </button>
                {isProtected && <WhitelistStar />}
                {item.classification && (
                  <span className="text-xs px-2 py-0.5 rounded-full" style={classificationStyles[item.classification] || classificationStyles.neutru}>
                    {item.classification}
                  </span>
                )}
                <span className="text-xs" style={{ color: "rgba(255,255,255,0.3)" }}>{item.page_name}</span>
                {item.is_hidden === 1 && (
                  <span className="text-xs px-2 py-0.5 rounded-full" title="Comentariu ascuns — la aprobare se va face unhide automat"
                    style={{ background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.4)" }}>
                    ascuns
                  </span>
                )}
              </div>
              <div className="flex flex-col items-end gap-1 flex-shrink-0 ml-2">
                <div className="flex items-center gap-1.5">
                  {getSlaStatus && getSlaStatus(item.created_time) === "red" && <span className="text-xs px-1.5 py-0.5 rounded-full font-bold" style={{ background: "rgba(239,68,68,0.2)", color: "#fca5a5" }}>🔴</span>}
                  {getSlaStatus && getSlaStatus(item.created_time) === "yellow" && <span className="text-xs px-1.5 py-0.5 rounded-full font-bold" style={{ background: "rgba(234,179,8,0.2)", color: "#fde047" }}>🟡</span>}
                  {getSlaStatus && getSlaStatus(item.created_time) === "green" && <span className="text-xs px-1.5 py-0.5 rounded-full font-bold" style={{ background: "rgba(34,197,94,0.2)", color: "#86efac" }}>🟢</span>}
                  <span className="text-xs" style={{ color: "rgba(255,255,255,0.3)" }}>{timeAgo(item.created_time)}</span>
                </div>
                <span className="text-xs" style={{ color: "rgba(255,255,255,0.45)" }}>{safeLocaleString(item.created_time)}</span>
              </div>
            </div>

            {/* Folosim PostMessage cu expand (B4) */}
            {item.post_message && <PostMessage message={item.post_message} maxLen={200} />}

            {/* Lantul de parinti */}
            {item.parent_chain && item.parent_chain.length > 0 && (
              <div className="space-y-1 mb-2">
                {item.parent_chain.map((p, i) => (
                  <div key={p.id} className="rounded-lg px-3 py-1.5"
                    style={{ background: "rgba(255,255,255,0.02)", borderLeft: "2px solid rgba(255,255,255,0.08)", marginLeft: `${indentFor(i)}px` }}>
                    <span className="text-xs font-semibold" style={{ color: "rgba(255,255,255,0.5)" }}>
                      💬 <button onClick={() => setProfileComment({ id: item.comment_id, from_id: p.from_id, from_name: p.from_name, classification: p.classification, is_blocked: 0, page_id: item.page_id })}
                        className="hover:text-indigo-300 transition-colors"
                        style={{ color: "rgba(255,255,255,0.7)", background: "none", border: "none", cursor: "pointer", padding: 0 }}>
                        {p.from_name}
                      </button>
                      {whitelistIds && whitelistIds.has && whitelistIds.has(p.from_id) && <WhitelistStar />}
                      :
                    </span>
                    {p.classification && <span className="ml-1 px-1.5 py-0.5 rounded-full text-xs" style={classificationStyles[p.classification] || classificationStyles.neutru}>{p.classification}</span>}
                    <p className="text-xs mt-0.5" style={{ color: "rgba(255,255,255,0.5)" }}>{p.message}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Mention block (cand comentariul tag-uieste un user) */}
            {item.mention && (
              <div className="rounded-lg px-3 py-1.5 mb-2"
                style={{
                  background: "rgba(167,139,250,0.06)",
                  borderLeft: "2px solid rgba(167,139,250,0.4)",
                  marginLeft: item.parent_chain && item.parent_chain.length > 0 ? `${indentFor(item.parent_chain.length)}px` : "0px"
                }}>
                <span className="text-xs font-semibold" style={{ color: "rgba(167,139,250,0.85)" }}>
                  💡 răspunde la <button onClick={() => setProfileComment({ id: item.comment_id, from_id: item.mention.from_id, from_name: item.mention.from_name, classification: item.mention.classification, is_blocked: 0, page_id: item.page_id })}
                    className="hover:text-indigo-200 transition-colors"
                    style={{ color: "#c4b5fd", background: "none", border: "none", cursor: "pointer", padding: 0, fontWeight: "bold" }}>
                    {item.mention.from_name}
                  </button>
                  {item.mention.classification && <span className="ml-1 px-1.5 py-0.5 rounded-full text-xs" style={classificationStyles[item.mention.classification] || classificationStyles.neutru}>{item.mention.classification}</span>}
                  :
                </span>
                <p className="text-xs mt-0.5 italic" style={{ color: "rgba(255,255,255,0.55)" }}>
                  «{item.mention.message && item.mention.message.length > 200 ? item.mention.message.slice(0, 200) + '…' : item.mention.message}»
                </p>
              </div>
            )}

            <div className="rounded-xl px-3 py-2.5 mb-3 text-sm"
              style={{
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.06)",
                marginLeft: item.parent_chain && item.parent_chain.length > 0 ? `${indentFor(item.parent_chain.length)}px` : "0px"
              }}>
              <TextWithLinks text={item.comment_message} style={{ color: "rgba(255,255,255,0.85)", fontSize: "0.875rem" }} />
            </div>

            {buildFbPermalink(item.post_id, item.comment_id, item.parent_id) && (
              <a href={buildFbPermalink(item.post_id, item.comment_id, item.parent_id)} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 mb-3 text-xs font-medium transition-all"
                style={{
                  background: "rgba(59,130,246,0.12)",
                  border: "1px solid rgba(59,130,246,0.35)",
                  color: "#93c5fd",
                  textDecoration: "none"
                }}>
                🔗 Link comentariu FB
              </a>
            )}

            <div className="rounded-xl px-3 py-2.5 mb-3" style={{ background: "rgba(99,102,241,0.08)", border: "1px solid rgba(99,102,241,0.2)" }}>
              <div className="flex items-center gap-1 mb-1">
                <span className="text-xs font-medium" style={{ color: "#a5b4fc" }}>🤖 Răspuns generat</span>
              </div>
              {editingReply?.replyId === item.reply_id ? (
                <textarea
                  className="w-full rounded-lg px-2 py-1.5 text-sm resize-none"
                  style={{ ...inputStyle, minHeight: "80px" }}
                  value={editingReply.message}
                  onChange={e => setEditingReply({ ...editingReply, message: e.target.value })}
                  autoFocus
                />
              ) : (
                <p className="text-sm text-white">{item.reply_message}</p>
              )}
            </div>

            <div className="flex gap-2 flex-wrap">
              {editingReply?.replyId === item.reply_id ? (
                <>
                  <LoadingButton
                    onClick={async () => { await approveReply(item.reply_id, editingReply.message); setEditingReply(null); }}
                    loadingText="Se trimite..."
                    className="px-4 py-1.5 rounded-xl text-xs font-semibold"
                    style={btnGreen}>
                    ✅ Trimite editatul
                  </LoadingButton>
                  <button onClick={() => setEditingReply(null)}
                    className="px-3 py-1.5 rounded-xl text-xs" style={btnGray}>
                    Anulează
                  </button>
                </>
              ) : (
                <>
                  <LoadingButton
                    onClick={() => approveReply(item.reply_id)}
                    loadingText="Se trimite..."
                    className="px-4 py-1.5 rounded-xl text-xs font-semibold"
                    style={btnGreen}>
                    ✅ Aprobă
                  </LoadingButton>
                  <button
                    onClick={() => setEditingReply({ replyId: item.reply_id, message: item.reply_message })}
                    className="px-3 py-1.5 rounded-xl text-xs font-medium"
                    style={btnPurple}>
                    ✏️ Editează
                  </button>
                  <LoadingButton
                    onClick={() => regenerateReply(item.reply_id)}
                    loadingText="Se generează..."
                    className="px-3 py-1.5 rounded-xl text-xs font-medium"
                    style={btnGray}>
                    🔄 Regenerează
                  </LoadingButton>
                  <LoadingButton
                    onClick={() => rejectReply(item.reply_id)}
                    loadingText="Se respinge..."
                    className="px-3 py-1.5 rounded-xl text-xs font-medium"
                    style={btnRed}>
                    ❌ Respinge
                  </LoadingButton>
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ============================================================
// INBOX — orchestrator
// ============================================================
function Inbox({ pages, onGoToHistoric, whitelistIds, onCountsChange }) {
  const [comments, setComments] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const perPage = 50;
  const [counts, setCounts] = useState({ unresolved: 0, hidden_count: 0, blocked_count: 0, resolved_count: 0, pending_count: 0, whitelist_critic_count: 0 });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("nerezolvate");
  const [pendingItems, setPendingItems] = useState([]);
  const [pendingTotal, setPendingTotal] = useState(0);
  const [editingReply, setEditingReply] = useState(null);
  const [sla, setSla] = useState({ sla_yellow_hours: 1, sla_red_hours: 3 });
  const [filter, setFilter] = useState("all");
  const [filterPage, setFilterPage] = useState("");
  const [filterAccount, setFilterAccount] = useState("");
  const [autopilot, setAutopilot] = useState(false);
  const [showBlockConfirm, setShowBlockConfirm] = useState(null);
  const [showUnblockConfirm, setShowUnblockConfirm] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [errorModal, setErrorModal] = useState(null);
  const [protectedAlert, setProtectedAlert] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [blockingSelected, setBlockingSelected] = useState(false);
  const [profileComment, setProfileComment] = useState(null);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [blockingAllTrolls, setBlockingAllTrolls] = useState(false);
  const [blockAllResult, setBlockAllResult] = useState(null);
  const [blockAllProgress, setBlockAllProgress] = useState({ current: 0, total: 0, blocked: 0 });
  const [blockSelectedProgress, setBlockSelectedProgress] = useState({ current: 0, total: 0, skipped: 0 });

  const pollingRef = useRef(null);
  const blockPollRef = useRef(null);
  const abortRef = useRef(null);
  const activeReplyRef = useRef(false);
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/settings/sla`).then(res => setSla(res.data)).catch(() => {});
    axios.get(`${API}/settings/autopilot`).then(res => setAutopilot(res.data.enabled)).catch(() => {});
  }, []);

  const getSlaStatus = useCallback((dateStr) => {
    if (!dateStr) return "green";
    const normalized = dateStr.includes('Z') || dateStr.includes('+') ? dateStr : dateStr.replace(' ', 'T') + 'Z';
    const hrs = (Date.now() - new Date(normalized).getTime()) / 3600000;
    if (hrs >= sla.sla_red_hours) return "red";
    if (hrs >= sla.sla_yellow_hours) return "yellow";
    return "green";
  }, [sla.sla_yellow_hours, sla.sla_red_hours]);

  const toggleAutopilot = async () => {
    const newVal = !autopilot;
    await axios.post(`${API}/settings/autopilot`, { enabled: newVal });
    setAutopilot(newVal);
  };

  const fetchInbox = useCallback(async (overrides = {}, isPolling = false) => {
    if (isPolling && activeReplyRef.current) return;
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();
    try {
      const t = overrides.tab ?? tab;
      const f = overrides.filter ?? filter;
      const p = overrides.filterPage ?? filterPage;
      const a = overrides.filterAccount ?? filterAccount;
      const offset = overrides.offset ?? (page * perPage);
      const s = overrides.search ?? search;
      const params = new URLSearchParams({ tab: t, filter: f, limit: perPage, offset });
      if (p) params.append("page_id", p);
      if (a) params.append("account_id", a);
      if (s && s.trim()) params.append("search", s.trim());
      const res = await axios.get(`${API}/comments/inbox?${params}`, { signal: abortRef.current.signal });
      setComments(res.data.comments);
      setTotal(res.data.total || 0);
      const newCounts = {
        unresolved: res.data.unresolved,
        hidden_count: res.data.hidden_count,
        blocked_count: res.data.blocked_count,
        resolved_count: res.data.resolved_count || 0,
      };
      setCounts(prev => ({ ...prev, ...newCounts }));
      // Propagăm contorul unresolved sus pentru badge-ul global (D2 — consolidare polling)
      if (onCountsChange) onCountsChange({ unresolved: res.data.unresolved, comments: res.data.comments || [] });
    } catch (e) {
      if (axios.isCancel(e) || e.name === 'CanceledError') return;
      console.error(e);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, filter, filterPage, filterAccount, page, search]);

  const fetchCounts = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterPage) params.append("page_id", filterPage);
      else if (filterAccount) params.append("account_id", filterAccount);
      const res = await axios.get(`${API}/comments/inbox/counts?${params}`);
      setCounts(prev => ({
        ...prev,
        unresolved: res.data.unresolved,
        hidden_count: res.data.hidden_count,
        blocked_count: res.data.blocked_count,
        resolved_count: res.data.resolved_count,
        pending_count: res.data.pending_count !== undefined ? res.data.pending_count : prev.pending_count,
        whitelist_critic_count: res.data.whitelist_critic_count !== undefined ? res.data.whitelist_critic_count : prev.whitelist_critic_count,
      }));
    } catch (e) { /* silent */ }
  }, [filterPage, filterAccount]);

  const fetchPending = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filter && filter !== "all") params.append("filter", filter);
      if (filterPage) params.append("page_id", filterPage);
      else if (filterAccount) params.append("account_id", filterAccount);
      const res = await axios.get(`${API}/comments/inbox/pending-approval?${params}`);
      setPendingItems(res.data.items || []);
      setPendingTotal(res.data.total || 0);
      setCounts(prev => ({ ...prev, pending_count: res.data.total || 0 }));
    } catch (e) { console.error(e); }
  }, [filter, filterPage, filterAccount]);

  // Apel counts la mount si la schimbare filtre
  useEffect(() => {
    fetchCounts();
    const interval = setInterval(fetchCounts, 30000);
    return () => clearInterval(interval);
  }, [fetchCounts]);

  // useEffect principal — fără search în deps (D4: search merge prin debounce)
  useEffect(() => {
    setLoading(true);
    fetchInbox();
    fetchPending();
    if (pollingRef.current) clearInterval(pollingRef.current);
    pollingRef.current = setInterval(() => {
      fetchInbox({}, true);
      fetchPending();
    }, 30000);
    return () => clearInterval(pollingRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, filter, filterPage, filterAccount, page, search]);

  const handleTab = (t) => { setTab(t); setFilter("all"); setPage(0); setSelectedIds(new Set()); if (t === "de_aprobat") fetchPending(); };

  // Debounce search (D4)
  const handleSearchInput = (val) => {
    setSearchInput(val);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setPage(0);
      setSearch(val);
    }, 400);
  };

  // Construim resolver page_id pentru hook
  const getPageId = useCallback((commentId) => {
    const c = comments.find(c => c.id === commentId);
    if (c?.page_id) return c.page_id;
    const pendingMatch = pendingItems.find(item => item.comment_id === commentId);
    if (pendingMatch?.page_id) return pendingMatch.page_id;
    return profileComment?.page_id;
  }, [comments, pendingItems, profileComment]);

  const actions = useCommentActions({
    onAfterAction: () => { fetchInbox(); fetchCounts(); },
    getPageId,
    whitelistIds,
  });

  // Wrappere care folosesc hook-ul dar adaugă confirmări/loading state
  const handleHide = async (commentId) => { await actions.hide(commentId); };
  const handleUnhide = async (commentId) => { await actions.unhide(commentId); };
  const handleResolve = async (commentId) => {
    await axios.post(`${API}/comments/${commentId}/resolve`);
    setComments(prev => prev.filter(c => c.id !== commentId));
    setCounts(prev => ({ ...prev, unresolved: Math.max(0, prev.unresolved - 1) }));
  };
  const handleUnresolve = async (commentId) => { await actions.unresolve(commentId); };

  // Reply + unhide auto pentru tab Ascunse (B2)
  const handleReplyAndUnhide = async (commentId, message, pageId) => {
    // Backend va face unhide automat la trimitere reply (per spec Update_14)
    await axios.post(`${API}/comments/${commentId}/reply?page_id=${pageId}`, { message });
    fetchInbox();
  };

  const handleBlock = async () => {
    const commentId = showBlockConfirm;
    const c = comments.find(c => c.id === commentId);
    const pageId = c?.page_id || pendingItems.find(item => item.comment_id === commentId)?.page_id || profileComment?.page_id;
    if (!pageId) return;
    try {
      await axios.post(`${API}/comments/${commentId}/block-user?page_id=${pageId}`);
    } catch (e) { console.error(e); }
    setShowBlockConfirm(null);
    setProfileComment(null);
    fetchInbox();
    fetchPending();
  };

  const handleDelete = async () => {
    const commentId = showDeleteConfirm;
    const c = comments.find(c => c.id === commentId);
    if (!c) { setShowDeleteConfirm(null); return; }
    try {
      await axios.delete(`${API}/comments/${commentId}?page_id=${c.page_id}`);
    } catch (e) { console.error(e); }
    setShowDeleteConfirm(null);
    fetchInbox();
  };

  const handleUnblock = async () => {
    const commentId = showUnblockConfirm;
    const c = comments.find(c => c.id === commentId);
    const pageId = c?.page_id || profileComment?.page_id;
    if (!pageId) { setShowUnblockConfirm(null); return; }
    try {
      await axios.post(`${API}/comments/${commentId}/unblock-user?page_id=${pageId}`);
    } catch (e) { console.error(e); }
    setShowUnblockConfirm(null);
    setProfileComment(null);
    fetchInbox();
  };

  // Blochează selecția — paralelizat backend (5 workeri) cu polling progres
  const handleBlockSelected = async () => {
    setBlockingSelected(true);
    const allIds = Array.from(selectedIds);
    const skipped = [];
    const idsToBlock = allIds.filter(id => {
      const c = comments.find(c => c.id === id);
      if (!c) return false;
      if (whitelistIds && whitelistIds.has && whitelistIds.has(c.from_id)) {
        skipped.push(c.from_name);
        return false;
      }
      return true;
    });

    if (idsToBlock.length === 0) {
      setBlockingSelected(false);
      if (skipped.length > 0) {
        setProtectedAlert({
          title: "Nimic de blocat",
          message: `Toți cei ${skipped.length} selectați sunt ⭐ protejați.`
        });
      }
      return;
    }

    setBlockSelectedProgress({ current: 0, total: idsToBlock.length, skipped: skipped.length });

    try {
      // Pornim blocarea in backend (paralelizat 5 workeri)
      await axios.post(`${API}/comments/block-selected`, { comment_ids: idsToBlock });

      // Pollam statusul la fiecare 500ms
      const pollStatus = async () => {
        try {
          const res = await axios.get(`${API}/comments/block-selected-status`);
          const s = res.data;
          setBlockSelectedProgress({ current: s.current, total: s.total, skipped: skipped.length });
          if (s.finished) return s;
          await new Promise(r => setTimeout(r, 500));
          return pollStatus();
        } catch (e) {
          console.error("polling err:", e);
          return null;
        }
      };
      const final = await pollStatus();

      setSelectedIds(new Set());
      setBlockingSelected(false);

      const blockedCount = final ? final.blocked : idsToBlock.length;
      const errCount = final ? final.errors : 0;
      let msg = `Blocați: ${blockedCount}`;
      if (errCount > 0) msg += `. Erori: ${errCount}`;
      if (skipped.length > 0) msg += `. ⭐ Săriți (protejați): ${skipped.length} — ${skipped.slice(0, 3).join(", ")}${skipped.length > 3 ? `, +${skipped.length - 3}` : ""}`;
      setProtectedAlert({ title: "Blocare selecție finalizată", message: msg });
    } catch (e) {
      console.error("Eroare blocare selectie:", e);
      setBlockingSelected(false);
      setProtectedAlert({ title: "Eroare", message: "Nu am putut porni blocarea. Încearcă din nou." });
    }

    setBlockSelectedProgress({ current: 0, total: 0, skipped: 0 });
    fetchInbox();
  };

  const blockAllTrolls = async () => {
    setBlockingAllTrolls(true); setBlockAllResult(null);
    setBlockAllProgress({ current: 0, total: 0, blocked: 0 });
    try {
      const params = filterPage ? `?page_id=${filterPage}` : "";
      await axios.post(`${API}/comments/inbox/block-trolls${params}`);
      if (blockPollRef.current) clearInterval(blockPollRef.current);
      blockPollRef.current = setInterval(async () => {
        try {
          const res = await axios.get(`${API}/comments/inbox/block-trolls-status`);
          setBlockAllProgress({ current: res.data.current, total: res.data.total, blocked: res.data.blocked });
          if (res.data.finished) {
            clearInterval(blockPollRef.current);
            setBlockingAllTrolls(false);
            setBlockAllResult(res.data);
            fetchInbox();
          }
        } catch (e) { clearInterval(blockPollRef.current); setBlockingAllTrolls(false); }
      }, 1000);
    } catch (e) { console.error(e); setBlockingAllTrolls(false); }
  };

  const approveReply = async (replyId, message) => {
    if (message) {
      setPendingItems(prev => prev.map(item =>
        item.reply_id === replyId ? { ...item, reply_message: message } : item
      ));
    }
    try {
      const body = message ? { message } : {};
      const res = await axios.post(`${API}/replies/${replyId}/approve`, body, {
        headers: { "Content-Type": "application/json" }
      });
      if (res.data?.error) {
        setErrorModal(res.data.error.message || "Eroare necunoscută Facebook");
      }
      await fetchPending(); fetchCounts();
    } catch (e) { console.error(e); }
  };

  const rejectReply = async (replyId) => {
    await axios.post(`${API}/replies/${replyId}/reject`);
    await fetchPending(); fetchCounts();
  };

  const regenerateReply = async (replyId) => {
    try {
      const res = await axios.post(`${API}/replies/${replyId}/regenerate`);
      setPendingItems(prev => prev.map(item =>
        item.reply_id === replyId ? { ...item, reply_message: res.data.message } : item
      ));
      if (editingReply?.replyId === replyId) {
        setEditingReply({ replyId, message: res.data.message });
      }
    } catch (e) { console.error(e); }
  };

  // useMemo pentru tab-uri și conturi (D5)
  const inboxTabs = useMemo(() => [
    { id: "nerezolvate", label: "Nerezolvate", count: counts.unresolved, countColor: "#fca5a5" },
    { id: "de_aprobat", label: "De aprobat", count: counts.pending_count || 0, countColor: "#c4b5fd" },
    { id: "ascunse", label: "Ascunse", count: counts.hidden_count, countColor: "#fde047" },
    { id: "blocate", label: "Blocate", count: counts.blocked_count, countColor: "#fdba74" },
    { id: "rezolvate", label: "Rezolvate", count: counts.resolved_count, countColor: "#86efac" },
    { id: "whitelist", label: "⭐ Whitelist", count: counts.whitelist_critic_count || 0, countColor: "#fca5a5" },
  ], [counts]);

  const classFilters = useMemo(() => [
    { id: "all", label: "Toate" },
    { id: "sustinator", label: "Susținători" },
    { id: "critic", label: "Critici" },
    { id: "troll", label: "Troli" },
    { id: "intrebare", label: "Întrebări" },
    { id: "neutru", label: "Neutri" },
    { id: "neclasificate", label: "Neclasificate" },
  ], []);

  const accounts = useMemo(() => {
    return [...new Map(pages.filter(p => p.account_id).map(p => [p.account_id, { id: p.account_id, name: p.account_name || `Cont ${p.account_id}` }])).values()];
  }, [pages]);

  // Calcul protejați din lista curentă (pentru afișare în filter=troll)
  const protectedInList = useMemo(() => {
    if (!whitelistIds || !whitelistIds.has) return 0;
    return comments.filter(c => whitelistIds.has(c.from_id)).length;
  }, [comments, whitelistIds]);

  return (
    <div>
      {profileComment && (
        <UserProfilePanel
          comment={profileComment}
          whitelistIds={whitelistIds}
          onClose={() => setProfileComment(null)}
          onBlock={() => { setShowBlockConfirm(profileComment.id); }}
          onUnblock={() => { setShowUnblockConfirm(profileComment.id); }}
          onReclassified={() => { setComments([]); fetchInbox(); }}
        />
      )}

      <ConfirmModal
        open={!!showBlockConfirm}
        title="Blochează utilizatorul"
        text="Ești sigur că vrei să blochezi acest utilizator?"
        confirmLabel="Blochează"
        loadingLabel="Se blochează..."
        confirmStyle={btnOrange}
        onConfirm={handleBlock}
        onCancel={() => setShowBlockConfirm(null)}
      />

      <ConfirmModal
        open={!!showUnblockConfirm}
        title="Deblochează utilizatorul"
        text="Ești sigur că vrei să deblochezi acest utilizator?"
        confirmLabel="Deblochează"
        loadingLabel="Se deblochează..."
        confirmStyle={btnGreen}
        onConfirm={handleUnblock}
        onCancel={() => setShowUnblockConfirm(null)}
      />

      <ConfirmModal
        open={!!showDeleteConfirm}
        title="Șterge comentariul"
        text="Comentariul va fi șters de pe Facebook."
        confirmLabel="Șterge"
        loadingLabel="Se șterge..."
        confirmStyle={btnRed}
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteConfirm(null)}
      />

      <ConfirmModal
        open={!!errorModal}
        title="Răspunsul nu a putut fi trimis"
        text={
          <>
            <p className="mb-2">Comentariul nu mai există pe Facebook sau a fost șters. Reply-ul a fost marcat ca eșuat și eliminat din lista de aprobare.</p>
            <p className="text-xs px-3 py-2 rounded-lg" style={{ background: "rgba(239,68,68,0.08)", color: "#fca5a5", border: "1px solid rgba(239,68,68,0.15)" }}>
              {errorModal}
            </p>
          </>
        }
        variant="error"
        confirmLabel="Am înțeles"
        confirmStyle={btnGray}
        onConfirm={() => setErrorModal(null)}
        hideCancel={true}
      />

      <ConfirmModal
        open={!!protectedAlert}
        title={protectedAlert?.title || "Acțiune blocată"}
        text={protectedAlert?.message}
        variant="error"
        confirmLabel="Am înțeles"
        confirmStyle={btnGray}
        onConfirm={() => setProtectedAlert(null)}
        hideCancel={true}
      />

      <InboxHeader
        inboxTabs={inboxTabs}
        tab={tab}
        onTab={handleTab}
        accounts={accounts}
        pages={pages}
        filterAccount={filterAccount}
        filterPage={filterPage}
        onFilterAccount={(v) => { setFilterAccount(v); setFilterPage(""); setPage(0); }}
        onFilterPage={(v) => { setFilterPage(v); setPage(0); }}
        search={searchInput}
        onSearch={handleSearchInput}
        classFilters={classFilters}
        filter={filter}
        onFilter={(v) => { setFilter(v); setPage(0); }}
        autopilot={autopilot}
        onToggleAutopilot={toggleAutopilot}
        onRefresh={() => fetchInbox()}
      />

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {tab !== "de_aprobat" && <span className="text-xs" style={{ color: "rgba(255,255,255,0.3)" }}>
          {total === 0 ? "0 comentarii" : `${page * perPage + 1}-${Math.min((page + 1) * perPage, total)} din ${total} comentarii`}
        </span>}
        {tab === "de_aprobat" && pendingTotal > 0 && <span className="text-xs" style={{ color: "rgba(255,255,255,0.3)" }}>
          {pendingTotal} răspunsuri de aprobat
        </span>}
        {filter === "troll" && tab === "nerezolvate" && protectedInList > 0 && (
          <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: "rgba(234,179,8,0.1)", color: "#fde047" }}>
            ⭐ {protectedInList} protejați (nu vor fi afectați)
          </span>
        )}
        {filter === "troll" && tab === "nerezolvate" && comments.length > 0 && !blockingAllTrolls && (
          <LoadingButton onClick={blockAllTrolls}
            loadingText="Se pornește..."
            className="px-3 py-1.5 rounded-xl text-xs font-medium"
            style={btnOrange}>
            🚫 Blochează toți trolii ({comments.length})
          </LoadingButton>
        )}
        {blockingAllTrolls && (
          <div className="flex items-center gap-2">
            <span className="text-xs" style={{ color: "#fdba74" }}>
              Blocare {blockAllProgress.current}/{blockAllProgress.total}...
            </span>
            <div className="w-24 rounded-full h-1.5" style={{ background: "rgba(255,255,255,0.06)" }}>
              <div className="h-1.5 rounded-full transition-all" style={{
                width: blockAllProgress.total > 0 ? `${Math.round((blockAllProgress.current / blockAllProgress.total) * 100)}%` : "5%",
                background: "linear-gradient(135deg,#f97316,#ef4444)"
              }}></div>
            </div>
          </div>
        )}
        {blockAllResult && !blockingAllTrolls && (
          <span className="text-xs px-2 py-1 rounded-lg" style={{ background: "rgba(34,197,94,0.1)", color: "#86efac" }}>
            ✓ Blocați: {blockAllResult.blocked} | Erori: {blockAllResult.errors}
          </span>
        )}
        <span className="text-xs ml-auto" style={{ color: "rgba(99,102,241,0.4)" }}>refresh la 30s</span>
      </div>

      {total > perPage && tab !== "de_aprobat" && (
        <div className="flex items-center justify-center gap-3 mb-4">
          <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
            className="px-4 py-2 rounded-xl text-sm font-medium disabled:opacity-30"
            style={btnGray}>← Anterior</button>
          <span className="text-xs" style={{ color: "rgba(255,255,255,0.4)" }}>
            {page + 1} / {Math.ceil(total / perPage)}
          </span>
          <button onClick={() => setPage(p => p + 1)} disabled={(page + 1) * perPage >= total}
            className="px-4 py-2 rounded-xl text-sm font-medium disabled:opacity-30"
            style={btnGray}>Următor →</button>
        </div>
      )}

      {loading && <div className="text-sm py-8 text-center" style={{ color: "rgba(255,255,255,0.25)" }}>Se incarca...</div>}

      {(filter === "troll" || filter === "critic") && (tab === "nerezolvate" || tab === "ascunse") && comments.length > 0 && (
        <div className="flex items-center gap-2 mb-3 p-2 rounded-xl" style={{ background: "rgba(99,102,241,0.05)", border: "1px solid rgba(99,102,241,0.15)" }}>
          <input type="checkbox"
            checked={selectedIds.size > 0 && selectedIds.size === comments.filter(c => !(whitelistIds && whitelistIds.has && whitelistIds.has(c.from_id))).length}
            onChange={() => {
              const eligible = comments.filter(c => !(whitelistIds && whitelistIds.has && whitelistIds.has(c.from_id)));
              if (selectedIds.size === eligible.length) setSelectedIds(new Set());
              else setSelectedIds(new Set(eligible.map(c => c.id)));
            }}
            className="accent-indigo-500 w-4 h-4 cursor-pointer" />
          <span className="text-xs" style={{ color: "rgba(255,255,255,0.5)" }}>
            {selectedIds.size > 0 ? `${selectedIds.size} selectate` : "Selectează toate (fără ⭐ protejați)"}
          </span>
          {selectedIds.size > 0 && !blockingSelected && (
            <button onClick={handleBlockSelected}
              className="px-3 py-1 rounded-lg text-xs font-medium ml-2"
              style={{ background: "rgba(249,115,22,0.15)", border: "1px solid rgba(249,115,22,0.3)", color: "#fdba74" }}>
              🚫 Blochează selecția ({selectedIds.size})
            </button>
          )}
          {blockingSelected && (
            <div className="flex items-center gap-2 ml-2">
              <span className="text-xs" style={{ color: "#fdba74" }}>
                Blocare {blockSelectedProgress.current}/{blockSelectedProgress.total}...
              </span>
              <div className="w-24 rounded-full h-1.5" style={{ background: "rgba(255,255,255,0.06)" }}>
                <div className="h-1.5 rounded-full transition-all" style={{
                  width: blockSelectedProgress.total > 0 ? `${Math.round((blockSelectedProgress.current / blockSelectedProgress.total) * 100)}%` : "5%",
                  background: "linear-gradient(135deg,#f97316,#ef4444)"
                }}></div>
              </div>
            </div>
          )}
          {selectedIds.size > 0 && (
            <button onClick={() => setSelectedIds(new Set())}
              className="px-2 py-1 rounded-lg text-xs" style={{ color: "rgba(255,255,255,0.4)" }}>
              Deselectează
            </button>
          )}
        </div>
      )}

      <div className="space-y-3">
        {!loading && tab !== "de_aprobat" && comments.map(comment => (
          <InboxItem
            key={comment.id}
            comment={comment}
            pages={pages}
            tab={tab}
            whitelistIds={whitelistIds}
            onHide={handleHide}
            onUnhide={handleUnhide}
            onBlock={(id) => setShowBlockConfirm(id)}
            onUnblock={(id) => setShowUnblockConfirm(id)}
            onResolve={handleResolve}
            onUnresolve={handleUnresolve}
            onGoToHistoric={onGoToHistoric}
            onShowProfile={setProfileComment}
            onDelete={(id) => setShowDeleteConfirm(id)}
            onReplyAndUnhide={handleReplyAndUnhide}
            slaStatus={getSlaStatus(comment.created_time)}
            isSelected={selectedIds.has(comment.id)}
            onToggleSelect={(id) => setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; })}
            showCheckbox={(filter === "troll" || filter === "critic") && (tab === "nerezolvate" || tab === "ascunse")}
            onReplyOpen={(open) => { activeReplyRef.current = open; }}
            onProtectedAttempt={(name, action) => setProtectedAlert({
              title: "Utilizator protejat",
              message: `⭐ ${name} este în lista de susținători protejați și nu poate fi ${action}. Scoate-l din whitelist mai întâi dacă vrei să procedezi.`
            })}
          />
        ))}
        {!loading && comments.length === 0 && tab !== "de_aprobat" && (
          <div className="text-center py-16 text-sm" style={{ color: "rgba(255,255,255,0.2)" }}>
            {tab === "rezolvate" ? "Niciun comentariu rezolvat" : tab === "ascunse" ? "Niciun comentariu ascuns" : tab === "blocate" ? "Niciun utilizator blocat" : "🎉 Niciun comentariu nerezolvat!"}
          </div>
        )}

        {tab === "de_aprobat" && (
          <PendingList
            pendingItems={pendingItems}
            editingReply={editingReply}
            setEditingReply={setEditingReply}
            approveReply={approveReply}
            rejectReply={rejectReply}
            regenerateReply={regenerateReply}
            setProfileComment={setProfileComment}
            whitelistIds={whitelistIds}
            getSlaStatus={getSlaStatus}
          />
        )}
      </div>

      {total > perPage && tab !== "de_aprobat" && (
        <div className="flex items-center justify-center gap-3 mt-6">
          <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
            className="px-4 py-2 rounded-xl text-sm font-medium disabled:opacity-30"
            style={btnGray}>← Anterior</button>
          <span className="text-xs" style={{ color: "rgba(255,255,255,0.4)" }}>
            {page + 1} / {Math.ceil(total / perPage)}
          </span>
          <button onClick={() => setPage(p => p + 1)} disabled={(page + 1) * perPage >= total}
            className="px-4 py-2 rounded-xl text-sm font-medium disabled:opacity-30"
            style={btnGray}>Următor →</button>
        </div>
      )}
    </div>
  );
}
// ============================================================
// COMMENT ITEM (Istoric) — cu profile click și ⭐
// ============================================================
const CommentItem = ({ comment, depth, view, pages, selectedPage, replyText, setReplyText, classifications, onHide, onUnhide, onBlock, onUnblock, onDelete, onGenerateAi, onSendReply, onShowProfile, whitelistIds }) => {
  const classification = comment.classification || classifications[comment.id];
  const hidden = view === "hidden";
  const isBlocked = comment.is_blocked;
  const isProtected = whitelistIds && whitelistIds.has && whitelistIds.has(comment.from_id);
  const borderColor = isProtected ? "rgba(234,179,8,0.4)" : isBlocked ? "rgba(249,115,22,0.3)" : depth > 0 ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.07)";

  return (
    <div className={depth > 0 ? "mt-2 pl-3" : ""} style={depth > 0 ? { borderLeft: "2px solid rgba(255,255,255,0.1)", marginLeft: `${indentFor(depth)}px` } : {}}>
      <div className="rounded-xl p-4" style={{ background: hidden ? "rgba(255,255,255,0.01)" : isBlocked ? "rgba(249,115,22,0.04)" : "rgba(255,255,255,0.03)", border: `1px solid ${borderColor}` }}>
        <div className="flex justify-between items-start mb-2">
          <div className="flex items-center gap-1.5 flex-wrap mt-1">
            {depth > 0 && <span style={{ color: "rgba(255,255,255,0.25)" }} className="text-xs">↳</span>}
            <button onClick={() => onShowProfile && onShowProfile(comment)}
              className="text-sm font-semibold text-white hover:text-indigo-300 transition-colors"
              style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}>
              {comment.from_name || "Utilizator anonim"}
              {isProtected && <WhitelistStar />}
            </button>
            {classification && <span className="text-xs px-2 py-0.5 rounded-full" style={classificationStyles[classification] || classificationStyles.neutru}>{classification}</span>}
            {isBlocked && <span className="text-xs px-2 py-0.5 rounded-full" style={btnOrange}>blocat</span>}
            <span className="text-xs" style={{ color: "rgba(255,255,255,0.25)" }}>{safeLocaleString(comment.created_time)}</span>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {hidden
              ? <LoadingButton onClick={() => onUnhide(comment.id)} loadingText="..." className="px-2 py-1 rounded-lg text-xs" style={btnGreen}>Afișează</LoadingButton>
              : (!isProtected && <LoadingButton onClick={() => onHide(comment.id)} loadingText="..." className="px-2 py-1 rounded-lg text-xs" style={btnGray}>Ascunde</LoadingButton>)}
            {isBlocked
              ? <LoadingButton onClick={() => onUnblock(comment.id)} loadingText="..." className="px-2 py-1 rounded-lg text-xs" style={btnOrange}>Deblochează</LoadingButton>
              : (!isProtected && <LoadingButton onClick={() => onBlock(comment.id)} loadingText="..." className="px-2 py-1 rounded-lg text-xs" style={btnOrange}>Blochează</LoadingButton>)}
            <LoadingButton onClick={() => onDelete(comment.id)} loadingText="..." className="px-2 py-1 rounded-lg text-xs" style={btnRed}>Șterge</LoadingButton>
          </div>
        </div>
        {comment.mention && (
          <div className="rounded-lg px-3 py-1.5 mb-2"
            style={{
              background: "rgba(167,139,250,0.06)",
              borderLeft: "2px solid rgba(167,139,250,0.4)"
            }}>
            <span className="text-xs font-semibold" style={{ color: "rgba(167,139,250,0.85)" }}>
              💡 răspunde la <button onClick={() => onShowProfile && onShowProfile({ id: comment.id, from_id: comment.mention.from_id, from_name: comment.mention.from_name, classification: comment.mention.classification, is_blocked: 0, page_id: comment.page_id })}
                className="hover:text-indigo-200 transition-colors"
                style={{ color: "#c4b5fd", background: "none", border: "none", cursor: "pointer", padding: 0, fontWeight: "bold" }}>
                {comment.mention.from_name}
              </button>
              {comment.mention.classification && <span className="ml-1 px-1.5 py-0.5 rounded-full text-xs" style={classificationStyles[comment.mention.classification] || classificationStyles.neutru}>{comment.mention.classification}</span>}
              :
            </span>
            <p className="text-xs mt-0.5 italic" style={{ color: "rgba(255,255,255,0.55)" }}>
              «{comment.mention.message && comment.mention.message.length > 200 ? comment.mention.message.slice(0, 200) + '…' : comment.mention.message}»
            </p>
          </div>
        )}
        <p className="text-sm mb-3" style={{ color: "rgba(255,255,255,0.7)" }}>{comment.message}</p>
        {!hidden && !isBlocked && (
          <div className="space-y-2">
            <textarea className="w-full rounded-xl px-3 py-2 text-sm resize-none" style={inputStyle}
              placeholder="Scrie un raspuns..." rows={2}
              value={replyText[comment.id] || ""}
              onChange={e => setReplyText(prev => ({ ...prev, [comment.id]: e.target.value }))} />
            <div className="flex gap-2">
              <LoadingButton onClick={() => onGenerateAi(comment)} loadingText="Se generează..." className="px-3 py-1.5 rounded-lg text-xs font-medium" style={btnPurple}>✨ AI</LoadingButton>
              <LoadingButton onClick={() => onSendReply(comment.id, replyText[comment.id])}
                disabled={!replyText[comment.id]} loadingText="Se trimite..."
                className="px-3 py-1.5 rounded-lg text-xs font-medium"
                style={replyText[comment.id] ? btnPrimary : btnGray}>Trimite</LoadingButton>
            </div>
          </div>
        )}
      </div>
      {comment.children?.map(child => (
        <CommentItem key={child.id} comment={child} depth={depth + 1} view={view}
          pages={pages} selectedPage={selectedPage} replyText={replyText} setReplyText={setReplyText}
          classifications={classifications} onHide={onHide} onUnhide={onUnhide}
          onBlock={onBlock} onUnblock={onUnblock} onDelete={onDelete}
          onGenerateAi={onGenerateAi} onSendReply={onSendReply}
          onShowProfile={onShowProfile} whitelistIds={whitelistIds} />
      ))}
    </div>
  );
};

// ============================================================
// ISTORIC — cu ConfirmModal peste tot și fix-uri
// ============================================================
function Istoric({ pages, initialPage, initialPost, whitelistIds }) {
  const [selectedPage, setSelectedPage] = useState(initialPage || "");
  const [posts, setPosts] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [reactions, setReactions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sendingAll, setSendingAll] = useState(false);
  const [blockingTrolls, setBlockingTrolls] = useState(false);
  const [blockProgress, setBlockProgress] = useState({ current: 0, total: 0, blocked: 0 });
  const [generatingReplies, setGeneratingReplies] = useState(false);
  const [generateProgress, setGenerateProgress] = useState({ current: 0, total: 0 });
  const [sendProgress, setSendProgress] = useState({ current: 0, total: 0 });
  const [replyText, setReplyText] = useState({});
  const [classifications, setClassifications] = useState({});
  const [view, setView] = useState("visible");
  const [showConfirm, setShowConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showBlockConfirm, setShowBlockConfirm] = useState(null);
  const [showBlockTrollsConfirm, setShowBlockTrollsConfirm] = useState(false);
  const [showDeletePostConfirm, setShowDeletePostConfirm] = useState(false);
  const [deletePostFromFb, setDeletePostFromFb] = useState(false);
  const [showBdOnlyConfirm, setShowBdOnlyConfirm] = useState(false);
  const [deletePostError, setDeletePostError] = useState(null);
  const [protectedAlert, setProtectedAlert] = useState(null);
  const [newComment, setNewComment] = useState("");
  const [commentPage, setCommentPage] = useState("");
  const [profileComment, setProfileComment] = useState(null);

  useEffect(() => { if (initialPage) { setSelectedPage(initialPage); fetchPosts(initialPage); } /* eslint-disable-next-line */ }, [initialPage]);
  useEffect(() => { if (initialPost && posts.length > 0) { const post = posts.find(p => p.id === initialPost); if (post) fetchComments(post); } /* eslint-disable-next-line */ }, [initialPost, posts]);

  const fetchPosts = async (pageId) => {
    setLoading(true);
    // F4: reset complet la schimbare pagină
    setSelectedPost(null); setComments([]); setReactions(null);
    setReplyText({}); setClassifications({}); setView("visible");
    setNewComment(""); setCommentPage("");
    const res = await axios.get(`${API}/pages/${pageId}/posts`);
    setPosts(res.data.posts); setLoading(false);
  };

  const loadComments = async (post) => {
    const [commentsRes, reactionsRes] = await Promise.all([
      axios.get(`${API}/posts/${post.id}/comments`),
      axios.get(`${API}/posts/${post.id}/reactions`)
    ]);
    setComments(commentsRes.data.comments); setReactions(reactionsRes.data);
  };

  const fetchComments = async (post) => {
    setSelectedPost(post); setLoading(true);
    setReplyText({}); setClassifications({}); setNewComment(""); setCommentPage("");
    await loadComments(post); setLoading(false);
  };

  const { startPolling: startGeneratePolling } = usePolling(
    selectedPost ? `${API}/ai/generate-all-replies-status?post_id=${selectedPost.id}&page_id=${selectedPage}` : null,
    {
      onProgress: (status) => { setGeneratingReplies(true); setGenerateProgress({ current: status.current, total: status.total }); },
      onFinish: async (status) => { setGeneratingReplies(false); setGenerateProgress({ current: status.current, total: status.total }); if (status.replies) setReplyText(prev => ({ ...prev, ...status.replies })); await loadComments(selectedPost); }
    }
  );

  const generateAllReplies = async () => { setGeneratingReplies(true); setGenerateProgress({ current: 0, total: 0 }); startGeneratePolling(`${API}/ai/generate-all-replies?post_id=${selectedPost.id}&page_id=${selectedPage}`); };

  const sendReply = async (commentId, message) => { await axios.post(`${API}/comments/${commentId}/reply?page_id=${selectedPage}`, { message }); setReplyText(prev => ({ ...prev, [commentId]: "" })); await loadComments(selectedPost); };

  const sendAllReplies = async () => {
    setSendingAll(true); setShowConfirm(false);
    const toSend = Object.entries(replyText).filter(([_, t]) => t?.trim());
    setSendProgress({ current: 0, total: toSend.length });
    for (let i = 0; i < toSend.length; i++) {
      const [commentId, message] = toSend[i];
      try { await axios.post(`${API}/comments/${commentId}/reply?page_id=${selectedPage}`, { message }); } catch (e) { }
      setSendProgress({ current: i + 1, total: toSend.length });
    }
    setSendingAll(false); setReplyText({}); setSendProgress({ current: 0, total: 0 }); await loadComments(selectedPost);
  };

  const generateAiReply = async (comment) => {
    // Endpoint inteligent — backend construieste singur thread-ul + classification + tot
    const rr = await axios.post(`${API}/ai/generate-reply-for-comment/${comment.id}`);
    setReplyText(prev => ({ ...prev, [comment.id]: rr.data.reply }));
    if (rr.data.classification) {
      setClassifications(prev => ({ ...prev, [comment.id]: rr.data.classification }));
    }
    await loadComments(selectedPost);
  };

  const deleteComment = async () => {
    await axios.delete(`${API}/comments/${showDeleteConfirm}?page_id=${selectedPage}`);
    setShowDeleteConfirm(null);
    await loadComments(selectedPost);
  };

  const hideComment = async (commentId) => {
    const c = comments.find(c => c.id === commentId);
    if (c && whitelistIds && whitelistIds.has && whitelistIds.has(c.from_id)) {
      setProtectedAlert({ title: "Utilizator protejat", message: `⭐ ${c.from_name} este în whitelist și nu poate fi ascuns.` });
      return;
    }
    await axios.post(`${API}/comments/${commentId}/hide?page_id=${selectedPage}`);
    await loadComments(selectedPost);
  };

  const unhideComment = async (commentId) => {
    await axios.post(`${API}/comments/${commentId}/unhide?page_id=${selectedPage}`);
    await loadComments(selectedPost);
  };

  const blockUser = async () => {
    const commentId = showBlockConfirm;
    const c = comments.find(c => c.id === commentId);
    if (c && whitelistIds && whitelistIds.has && whitelistIds.has(c.from_id)) {
      setShowBlockConfirm(null);
      setProtectedAlert({ title: "Utilizator protejat", message: `⭐ ${c.from_name} este în whitelist și nu poate fi blocat.` });
      return;
    }
    await axios.post(`${API}/comments/${commentId}/block-user?page_id=${selectedPage}`);
    setShowBlockConfirm(null);
    await loadComments(selectedPost);
  };

  const requestBlock = (commentId) => {
    const c = comments.find(c => c.id === commentId);
    if (c && whitelistIds && whitelistIds.has && whitelistIds.has(c.from_id)) {
      setProtectedAlert({ title: "Utilizator protejat", message: `⭐ ${c.from_name} este în whitelist și nu poate fi blocat.` });
      return;
    }
    setShowBlockConfirm(commentId);
  };

  const unblockUser = async (commentId) => { await axios.post(`${API}/comments/${commentId}/unblock-user?page_id=${selectedPage}`); await loadComments(selectedPost); };

  const { startPolling: startBlockPolling } = usePolling(
    selectedPost ? `${API}/posts/${selectedPost.id}/block-trolls-status?page_id=${selectedPage}` : null,
    {
      onProgress: (status) => { setBlockingTrolls(true); setBlockProgress({ current: status.current, total: status.total, blocked: status.blocked }); },
      onFinish: async (status) => { setBlockingTrolls(false); setBlockProgress({ current: status.current, total: status.total, blocked: status.blocked }); await loadComments(selectedPost); }
    }
  );

  const blockAllTrolls = async () => { setBlockingTrolls(true); setBlockProgress({ current: 0, total: 0, blocked: 0 }); startBlockPolling(`${API}/posts/${selectedPost.id}/block-trolls?page_id=${selectedPage}`); };

  const deletePost = async (fromFb) => {
    const shouldDeleteFromFb = fromFb !== undefined ? fromFb : deletePostFromFb;
    try {
      const res = await axios.delete(`${API}/posts/${selectedPost.id}?page_id=${selectedPage}&delete_from_fb=${shouldDeleteFromFb}`);
      if (res.data.success) {
        setShowDeletePostConfirm(false);
        setShowBdOnlyConfirm(false);
        setDeletePostFromFb(false);
        setSelectedPost(null);
        setComments([]);
        const postsRes = await axios.get(`${API}/pages/${selectedPage}/posts`);
        setPosts(postsRes.data.posts);
      } else {
        // A2: înlocuit alert() cu modal stilizat
        setShowDeletePostConfirm(false);
        setShowBdOnlyConfirm(false);
        setDeletePostError("Eroare la ștergerea postării de pe Facebook. Postarea nu a fost ștearsă.");
      }
    } catch (e) {
      setShowDeletePostConfirm(false);
      setShowBdOnlyConfirm(false);
      setDeletePostError("Eroare la ștergerea postării. Verifică conexiunea sau reîncearcă.");
    }
  };

  const postComment = async () => {
    if (!newComment.trim() || !commentPage) return;
    try { await axios.post(`${API}/posts/${selectedPost.id}/comment?page_id=${commentPage}`, { message: newComment }); setNewComment(""); await loadComments(selectedPost); } catch (e) { console.error(e); }
  };

  // useMemo pentru calcule scumpe (D1)
  const attachments = useMemo(() => {
    if (!selectedPost?.attachments_json) return [];
    try { return JSON.parse(selectedPost.attachments_json) || []; } catch { return []; }
  }, [selectedPost?.attachments_json]);

  const buildTree = (list) => {
    const map = {}; const roots = [];
    list.forEach(c => { map[c.id] = { ...c, children: [] }; });
    list.forEach(c => { if (c.parent_id && map[c.parent_id]) map[c.parent_id].children.push(map[c.id]); else roots.push(map[c.id]); });
    return roots;
  };

  const visibleComments = useMemo(() => comments.filter(c => !c.is_hidden && !c.is_blocked), [comments]);
  const hiddenComments = useMemo(() => comments.filter(c => c.is_hidden && !c.is_blocked), [comments]);
  const blockedComments = useMemo(() => comments.filter(c => c.is_blocked), [comments]);
  const displayedComments = view === "visible" ? visibleComments : view === "hidden" ? hiddenComments : blockedComments;
  const commentTree = useMemo(() => buildTree(displayedComments), [displayedComments]);

  const repliesCount = Object.values(replyText).filter(t => t?.trim()).length;
  const reactionEmojis = { like: "👍", love: "❤️", haha: "😂", wow: "😮", sad: "😢", angry: "😠" };

  return (
    <div>
      {profileComment && (
        <UserProfilePanel
          comment={profileComment}
          whitelistIds={whitelistIds}
          onClose={() => setProfileComment(null)}
          onBlock={() => { setShowBlockConfirm(profileComment.id); setProfileComment(null); }}
          onUnblock={async () => { await unblockUser(profileComment.id); setProfileComment(null); }}
          onReclassified={() => loadComments(selectedPost)}
        />
      )}

      <ConfirmModal
        open={showConfirm}
        title="Confirmare trimitere"
        text={<>Trimiți <span className="text-white font-bold">{repliesCount} răspunsuri</span> pe Facebook?</>}
        confirmLabel="Da, trimite toate"
        loadingLabel="Se trimit..."
        confirmStyle={btnPrimary}
        onConfirm={sendAllReplies}
        onCancel={() => setShowConfirm(false)}
      />

      <ConfirmModal
        open={!!showDeleteConfirm}
        title="Șterge comentariul"
        text="Comentariul va fi șters de pe Facebook."
        confirmLabel="Șterge"
        loadingLabel="Se șterge..."
        confirmStyle={btnRed}
        onConfirm={deleteComment}
        onCancel={() => setShowDeleteConfirm(null)}
      />

      {showDeletePostConfirm && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: "rgba(0,0,0,0.8)" }}>
          <div className="rounded-2xl p-6 max-w-md w-full mx-4" style={card}>
            <h3 className="text-lg font-bold mb-2 text-white">Șterge postarea</h3>
            <p className="text-sm mb-3" style={{ color: "rgba(255,255,255,0.5)" }}>
              {selectedPost?.message?.substring(0, 60) || "Fără text"}...
            </p>
            <label className="flex items-center gap-2 mb-4 cursor-pointer">
              <input type="checkbox" checked={deletePostFromFb}
                onChange={e => setDeletePostFromFb(e.target.checked)}
                className="accent-red-500" />
              <span className="text-sm" style={{ color: "#fca5a5" }}>Șterge și de pe Facebook</span>
            </label>
            <div className="flex gap-3">
              <LoadingButton
                onClick={async () => {
                  if (!deletePostFromFb) {
                    setShowDeletePostConfirm(false);
                    setShowBdOnlyConfirm(true);
                  } else {
                    await deletePost(true);
                  }
                }}
                loadingText="Se șterge..."
                className="flex-1 py-2 rounded-xl text-sm font-medium justify-center"
                style={btnRed}>
                Șterge
              </LoadingButton>
              <button
                onClick={() => { setShowDeletePostConfirm(false); setDeletePostFromFb(false); }}
                className="flex-1 py-2 rounded-xl text-sm font-medium" style={btnGray}>
                Anulează
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        open={showBdOnlyConfirm}
        title="Atenție!"
        text="Postarea va rămâne pe Facebook dar va fi ștearsă din baza de date locală. Ești sigur?"
        confirmLabel="Da, șterge din BD"
        loadingLabel="Se șterge..."
        confirmStyle={btnRed}
        onConfirm={() => deletePost(false)}
        onCancel={() => setShowBdOnlyConfirm(false)}
      />

      <ConfirmModal
        open={!!deletePostError}
        title="Eroare ștergere postare"
        text={deletePostError}
        variant="error"
        confirmLabel="Am înțeles"
        confirmStyle={btnGray}
        onConfirm={() => setDeletePostError(null)}
        hideCancel={true}
      />

      <ConfirmModal
        open={showBlockTrollsConfirm}
        title="Blochează trolii"
        text="Toți utilizatorii clasificați ca troli vor fi blocați pe Facebook. Susținătorii ⭐ vor fi excluși automat."
        confirmLabel="Da, blochează"
        loadingLabel="Se pornește..."
        confirmStyle={btnOrange}
        onConfirm={async () => { setShowBlockTrollsConfirm(false); await blockAllTrolls(); }}
        onCancel={() => setShowBlockTrollsConfirm(false)}
      />

      <ConfirmModal
        open={!!showBlockConfirm}
        title="Blochează utilizatorul"
        text="Ești sigur că vrei să blochezi acest utilizator?"
        confirmLabel="Blochează"
        loadingLabel="Se blochează..."
        confirmStyle={btnOrange}
        onConfirm={blockUser}
        onCancel={() => setShowBlockConfirm(null)}
      />

      <ConfirmModal
        open={!!protectedAlert}
        title={protectedAlert?.title || "Utilizator protejat"}
        text={protectedAlert?.message}
        variant="error"
        confirmLabel="Am înțeles"
        confirmStyle={btnGray}
        onConfirm={() => setProtectedAlert(null)}
        hideCancel={true}
      />

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <label className="text-xs mb-1.5 block" style={{ color: "rgba(255,255,255,0.4)" }}>Pagina</label>
          <select className="w-full rounded-xl px-3 py-2.5 text-sm" style={inputStyle}
            value={selectedPage} onChange={e => { setSelectedPage(e.target.value); fetchPosts(e.target.value); }}>
            <option style={{ color: "black" }} value="">Selecteaza pagina...</option>
            {pages.map(p => <option style={{ color: "black" }} key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs mb-1.5 block" style={{ color: "rgba(255,255,255,0.4)" }}>Postare {posts.length > 0 && `(${posts.length})`}</label>
          <select className="w-full rounded-xl px-3 py-2.5 text-sm" style={inputStyle}
            value={selectedPost?.id || ""}
            onChange={e => { const post = posts.find(p => p.id === e.target.value); if (post) fetchComments(post); }}
            disabled={!selectedPage || loading}>
            <option style={{ color: "black" }} value="">Selecteaza postarea...</option>
            {posts.map(p => <option style={{ color: "black" }} key={p.id} value={p.id}>{new Date(p.created_time).toLocaleDateString('ro-RO')} — {p.message?.substring(0, 50) || "Fara text"} 💬 {p.comment_count || 0}</option>)}
          </select>
        </div>
      </div>

      {selectedPost && (
        <div className="rounded-2xl p-5 mb-4" style={{ ...card, border: "1px solid rgba(99,102,241,0.2)" }}>
          <div className="flex flex-col gap-2 mb-3">
            <div className="flex items-center justify-between">
              <span className="text-xs" style={{ color: "rgba(255,255,255,0.3)" }}>{safeLocaleString(selectedPost.created_time)}</span>
              <button onClick={() => setShowDeletePostConfirm(true)} className="px-2 py-1 rounded-lg text-xs" style={btnRed}>🗑 Șterge</button>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap mt-1">
              {generatingReplies && <div className="flex items-center gap-2"><span className="text-xs" style={{ color: "#c4b5fd" }}>Generare {generateProgress.current}/{generateProgress.total}...</span><div className="w-20 rounded-full h-1" style={{ background: "rgba(255,255,255,0.06)" }}><div className="h-1 rounded-full" style={{ width: generateProgress.total > 0 ? `${Math.round((generateProgress.current / generateProgress.total) * 100)}%` : "5%", background: "linear-gradient(135deg,#6366f1,#8b5cf6)" }}></div></div></div>}
              {!generatingReplies && visibleComments.length > 0 && <LoadingButton onClick={generateAllReplies} loadingText="Se pornește..." className="px-3 py-1.5 rounded-xl text-xs font-medium" style={btnPurple}>✨ Genereaza reply-uri ({visibleComments.length})</LoadingButton>}
              {blockingTrolls && <div className="flex items-center gap-2"><span className="text-xs" style={{ color: "#fdba74" }}>Blocare {blockProgress.current}/{blockProgress.total}...</span><div className="w-20 rounded-full h-1" style={{ background: "rgba(255,255,255,0.06)" }}><div className="h-1 rounded-full" style={{ width: blockProgress.total > 0 ? `${Math.round((blockProgress.current / blockProgress.total) * 100)}%` : "5%", background: "linear-gradient(135deg,#f97316,#ef4444)" }}></div></div></div>}
              {!blockingTrolls && comments.some(c => c.classification === 'troll' && !c.is_blocked) && <button onClick={() => setShowBlockTrollsConfirm(true)} className="px-3 py-1.5 rounded-xl text-xs font-medium" style={btnOrange}>Blocheaza trolii</button>}
              {sendingAll && <div className="flex items-center gap-2"><span className="text-xs" style={{ color: "#86efac" }}>Trimitere {sendProgress.current}/{sendProgress.total}...</span><div className="w-20 rounded-full h-1" style={{ background: "rgba(255,255,255,0.06)" }}><div className="h-1 rounded-full" style={{ width: sendProgress.total > 0 ? `${Math.round((sendProgress.current / sendProgress.total) * 100)}%` : "5%", background: "linear-gradient(135deg,#22c55e,#16a34a)" }}></div></div></div>}
              {!sendingAll && repliesCount > 0 && <button onClick={() => setShowConfirm(true)} className="px-3 py-1.5 rounded-xl text-xs font-semibold" style={btnGreen}>Trimite toate ({repliesCount})</button>}
              <div className="flex gap-1 rounded-xl p-1" style={{ background: "rgba(255,255,255,0.04)" }}>
                {[{ id: "visible", label: `Vizibile (${visibleComments.length})`, active: "rgba(34,197,94,0.2)", activeColor: "#86efac" }, { id: "hidden", label: `Ascunse (${hiddenComments.length})`, active: "rgba(239,68,68,0.15)", activeColor: "#fca5a5" }, { id: "blocked", label: `Blocati (${blockedComments.length})`, active: "rgba(249,115,22,0.15)", activeColor: "#fdba74" }].map(tab => (
                  <button key={tab.id} onClick={() => setView(tab.id)} className="px-3 py-1 rounded-lg text-xs font-medium transition-all"
                    style={view === tab.id ? { background: tab.active, color: tab.activeColor } : { color: "rgba(255,255,255,0.35)" }}>{tab.label}</button>
                ))}
              </div>
            </div>
          </div>
          <p className="text-sm whitespace-pre-wrap mb-3" style={{ color: "rgba(255,255,255,0.8)" }}>{selectedPost.message || "Fara text"}</p>
          {attachments.length > 0 && (
            <div className="grid grid-cols-3 gap-2 mb-3">
              {attachments.map((attachment, idx) => {
                const imgSrc = attachment?.media?.image?.src;
                const subAttachments = attachment?.subattachments?.data || [];
                if (subAttachments.length > 0) return subAttachments.map((sub, sidx) => sub?.media?.image?.src && <img key={`${idx}-${sidx}`} src={sub.media.image.src} alt="" className="rounded-xl w-full h-28 object-cover" />);
                return imgSrc ? <img key={idx} src={imgSrc} alt="" className="rounded-xl w-full h-28 object-cover" /> : null;
              })}
            </div>
          )}
          {reactions && reactions.total > 0 && (
            <div className="flex gap-3 pt-3" style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
              {Object.entries(reactionEmojis).map(([type, emoji]) => reactions[type] > 0 && <span key={type} className="flex items-center gap-1 text-sm"><span>{emoji}</span><span className="text-xs" style={{ color: "rgba(255,255,255,0.4)" }}>{reactions[type]}</span></span>)}
              <span className="text-xs ml-auto flex items-center gap-3" style={{ color: "rgba(255,255,255,0.25)" }}>
                <span className="flex items-center gap-1"><span>💬</span><span>{comments.length}</span></span>
                <span>Total reacții: {reactions.total}</span>
              </span>
            </div>
          )}
          <div className="mt-4 pt-4" style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
            <p className="text-xs mb-2" style={{ color: "rgba(255,255,255,0.4)" }}>💬 Comentariu nou pe această postare</p>
            <div className="mb-2">
              <select className="w-full rounded-xl px-3 py-2 text-sm" style={inputStyle} value={commentPage} onChange={e => setCommentPage(e.target.value)}>
                <option style={{ color: "black" }} value="">Selecteaza pagina cu care comentezi...</option>
                {pages.map(p => <option style={{ color: "black" }} key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div className="flex gap-2">
              <textarea className="flex-1 rounded-xl px-3 py-2 text-sm resize-none" style={inputStyle} placeholder="Scrie un comentariu..." rows={2} value={newComment} onChange={e => setNewComment(e.target.value)} />
              <div className="flex flex-col gap-2">
                <LoadingButton
                  onClick={async () => {
                    if (!selectedPost || !commentPage) return;
                    try {
                      const r = await axios.post(`${API}/ai/generate-comment-for-post/${selectedPost.id}?page_id=${commentPage}`);
                      setNewComment(r.data.comment || "");
                    } catch (e) { console.error(e); }
                  }}
                  disabled={!commentPage}
                  loadingText="..."
                  className="px-3 py-1.5 rounded-xl text-xs font-medium"
                  style={commentPage ? btnPurple : btnGray}
                >✨ AI</LoadingButton>
                <LoadingButton onClick={postComment} disabled={!newComment.trim() || !commentPage} loadingText="..." className="px-4 rounded-xl text-sm font-semibold" style={newComment.trim() && commentPage ? btnPrimary : btnGray}>Posteaza</LoadingButton>
              </div>
            </div>
          </div>
        </div>
      )}

      {loading && <div className="text-sm py-8 text-center" style={{ color: "rgba(255,255,255,0.25)" }}>Se incarca...</div>}
      <div className="space-y-3">
        {commentTree.map(comment => (
          <CommentItem key={comment.id} comment={comment} depth={0} view={view}
            pages={pages} selectedPage={selectedPage} replyText={replyText} setReplyText={setReplyText}
            classifications={classifications} onHide={hideComment} onUnhide={unhideComment}
            onBlock={requestBlock} onUnblock={unblockUser}
            onDelete={(id) => setShowDeleteConfirm(id)} onGenerateAi={generateAiReply} onSendReply={sendReply}
            onShowProfile={setProfileComment} whitelistIds={whitelistIds} />
        ))}
        {!loading && selectedPost && displayedComments.length === 0 && (
          <div className="text-sm text-center py-12" style={{ color: "rgba(255,255,255,0.2)" }}>
            {view === "hidden" ? "Niciun comentariu ascuns" : view === "blocked" ? "Niciun utilizator blocat" : "Niciun comentariu vizibil"}
          </div>
        )}
        {!selectedPost && <div className="text-sm text-center py-16" style={{ color: "rgba(255,255,255,0.2)" }}>Selecteaza o pagina si o postare</div>}
      </div>
    </div>
  );
}

// ============================================================
// MAIN COMMENTS — wrapper cu tab-uri și notificări
// ============================================================
export default function Comments() {
  const [activeTab, setActiveTab] = useState("inbox");
  const [pages, setPages] = useState([]);
  const [inboxUnresolved, setInboxUnresolved] = useState(0);
  const [historicPage, setHistoricPage] = useState(null);
  const [historicPost, setHistoricPost] = useState(null);
  const [toasts, setToasts] = useState([]);
  const [whitelistIds, setWhitelistIds] = useState(new Map());

  const prevUnresolvedRef = useRef(null);
  const notifiedSlaRef = useRef(new Set());
  const slaSettingsRef = useRef({ sla_yellow_hours: 1, sla_red_hours: 3 });

  useEffect(() => {
    fetchPages();
    getWhitelist().then(wl => setWhitelistIds(wl));
    // Reîncărcăm whitelist la focus (D3: invalidare)
    const onFocus = () => getWhitelist(true).then(wl => setWhitelistIds(wl));
    window.addEventListener('focus', onFocus);
    // Citim SLA settings o singură dată
    axios.get(`${API}/settings/sla`).then(res => { slaSettingsRef.current = res.data; }).catch(() => {});
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  const fetchPages = async () => { const res = await axios.get(`${API}/pages`); setPages(res.data.pages); };

  const playBeep = (frequency = 440, duration = 200) => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      oscillator.frequency.value = frequency;
      oscillator.type = 'sine';
      gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration / 1000);
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + duration / 1000);
    } catch (e) {}
  };

  // Toast-uri urgente rămân până click pe ✕ (B3)
  const sendNotification = (title, body, urgent = false) => {
    if (urgent) {
      playBeep(880, 200);
      setTimeout(() => playBeep(880, 200), 300);
    } else {
      playBeep(440, 150);
    }
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, title, body, urgent }]);
    if (!urgent) {
      setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 5000);
    }
  };

  // Callback de la Inbox cu contorul actual + comentariile pentru SLA detection (D2)
  const handleInboxCounts = useCallback(({ unresolved, comments }) => {
    setInboxUnresolved(unresolved);

    // Notificare comentarii noi
    if (prevUnresolvedRef.current !== null && unresolved > prevUnresolvedRef.current) {
      const diff = unresolved - prevUnresolvedRef.current;
      sendNotification(
        `📬 ${diff} comentari${diff === 1 ? 'u nou' : 'i noi'}`,
        `Ai ${unresolved} comentarii nerezolvate în Inbox`,
        false
      );
    }
    prevUnresolvedRef.current = unresolved;

    // Detectare SLA depășit (DOAR pentru comentarii nerezolvate)
    const { sla_yellow_hours, sla_red_hours } = slaSettingsRef.current;
    let redCount = 0; let yellowCount = 0;
    (comments || []).filter(c => !c.is_resolved && !c.is_hidden && !c.is_blocked).forEach(c => {
      if (!c.created_time) return;
      if (notifiedSlaRef.current.has(c.id)) return;
      const normalized = c.created_time.includes('Z') || c.created_time.includes('+') ? c.created_time : c.created_time.replace(' ', 'T') + 'Z';
      const hrs = (Date.now() - new Date(normalized).getTime()) / 3600000;
      if (hrs >= sla_red_hours) { redCount++; notifiedSlaRef.current.add(c.id); }
      else if (hrs >= sla_yellow_hours) { yellowCount++; notifiedSlaRef.current.add(c.id); }
    });
    if (redCount > 0) {
      sendNotification(`🔴 ${redCount} comentari${redCount === 1 ? 'u depășit' : 'i depășiți'}`, `Peste ${sla_red_hours}h fără răspuns!`, true);
    } else if (yellowCount > 0) {
      sendNotification(`🟡 ${yellowCount} comentari${yellowCount === 1 ? 'u în așteptare' : 'i în așteptare'}`, `Peste ${sla_yellow_hours}h fără răspuns`, false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goToHistoric = (comment) => {
    setHistoricPage(comment.page_id);
    setHistoricPost(comment.post_id);
    setActiveTab("istoric");
  };

  return (
    <div>
      <style>{toastStyles}</style>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-white">Comentarii</h2>
        <p className="text-sm mt-0.5" style={{ color: "rgba(255,255,255,0.35)" }}>Engagement si raspunsuri in timp real</p>
      </div>

      <div className="flex items-center gap-2 mb-6">
        <button onClick={() => setActiveTab("inbox")}
          className="px-4 py-2 rounded-xl text-sm font-medium transition-all flex items-center gap-2"
          style={activeTab === "inbox" ? btnPrimary : btnGray}>
          📬 Inbox
          {inboxUnresolved > 0 && (
            <span className="text-xs px-1.5 py-0.5 rounded-full font-bold" style={{ background: "rgba(239,68,68,0.3)", color: "#fca5a5" }}>
              {inboxUnresolved}
            </span>
          )}
        </button>
        <button onClick={() => setActiveTab("istoric")}
          className="px-4 py-2 rounded-xl text-sm font-medium transition-all"
          style={activeTab === "istoric" ? btnPrimary : btnGray}>
          📋 Istoric
        </button>
      </div>

      <div className="fixed bottom-6 right-6 z-50 space-y-3" style={{ maxWidth: "320px" }}>
        {toasts.map(toast => (
          <div key={toast.id} className="rounded-2xl p-4 shadow-2xl"
            style={{
              background: toast.urgent ? "linear-gradient(135deg,rgba(239,68,68,0.15),rgba(239,68,68,0.05))" : "linear-gradient(135deg,rgba(99,102,241,0.2),rgba(99,102,241,0.05))",
              border: toast.urgent ? "1px solid rgba(239,68,68,0.4)" : "1px solid rgba(99,102,241,0.4)",
              backdropFilter: "blur(20px)",
              animation: "slideIn 0.3s ease"
            }}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-white mb-0.5">{toast.title}</p>
                <p className="text-xs" style={{ color: "rgba(255,255,255,0.6)" }}>{toast.body}</p>
              </div>
              <button onClick={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
                className="text-xs px-2 py-1 rounded-lg flex-shrink-0"
                style={{ background: "rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.5)" }}>✕</button>
            </div>
            {/* Bara de progress doar pentru toast-uri non-urgente (B3) */}
            {!toast.urgent && (
              <div className="mt-2 rounded-full h-0.5 overflow-hidden" style={{ background: "rgba(255,255,255,0.1)" }}>
                <div className="h-0.5 rounded-full" style={{
                  width: "100%",
                  background: "#a5b4fc",
                  animation: "shrink 5s linear forwards"
                }}></div>
              </div>
            )}
          </div>
        ))}
      </div>

      {activeTab === "inbox" && <Inbox pages={pages} onGoToHistoric={goToHistoric} whitelistIds={whitelistIds} onCountsChange={handleInboxCounts} />}
      {activeTab === "istoric" && <Istoric pages={pages} initialPage={historicPage} initialPost={historicPost} whitelistIds={whitelistIds} />}
    </div>
  );
}
